import { getApiBaseUrl } from '@/config/environment';
import type * as OrderTypes from '@/types/order';
import type * as MktTypes from '@/types/market';

// API Configuration
const API_BASE_URL = getApiBaseUrl();

// API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
}

export interface LoginRequest {
  identifier: string;
  identifierType: 'email' | 'phone';
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  user: {
    id: string;
    email: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    name?: string; // For Google OAuth
    picture?: string; // For Google OAuth
    dateOfBirth?: string;
    emailVerified: boolean;
    phoneVerified?: boolean;
    isActive: boolean;
    isPremium?: boolean;
    accountType: string;
    lastLogin?: string;
    profileCompletionPercentage?: number;
    createdAt?: string;
    isProfileComplete?: boolean;
    profileMode?: 'public' | 'private';
  };
}

export interface RegisterRequest {
  identifier: string;
  identifierType: 'email' | 'phone';
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
}

export interface RegisterResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    isActive: boolean;
    emailVerified: boolean;
    phoneVerified: boolean;
    createdAt: string;
  };
}

// API Service Class
class ApiService {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  // Generic request method (retryOn401: internal flag to avoid infinite refresh retry)
  private async request<T>(
    endpoint: string,
    options: RequestInit & { _retryOn401?: boolean } = {}
  ): Promise<ApiResponse<T>> {
    const { _retryOn401 = true, ...fetchOptions } = options;
    // Normalize URL to avoid double slashes
    const baseURL = this.baseURL.replace(/\/+$/, ''); // Remove trailing slashes
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${baseURL}${normalizedEndpoint}`;
    
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Attach Authorization header if access token exists
    const { accessToken } = this.getTokens();
    if (accessToken) {
      defaultHeaders['Authorization'] = `Bearer ${accessToken}`;
    }

    const config: RequestInit = {
      ...fetchOptions,
      headers: {
        ...defaultHeaders,
        ...(fetchOptions.headers as Record<string, string>),
      },
    };

    // Create an AbortController for timeout
    const controller = new AbortController();
    let timeoutId: NodeJS.Timeout | null = null;

    try {
      console.log('Making API request to:', url);
      
      timeoutId = setTimeout(() => controller.abort(), 25000); // 25 second timeout (backend may be cold-starting)
      
      const response = await fetch(url, {
        ...config,
        signal: controller.signal
      });
      
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorCode: string | undefined;
        let fieldErrors: Record<string, string> | undefined;

        try {
          const errorData = JSON.parse(errorText);
          // Support both the legacy { message } shape and the v1 contract
          // error envelope { error: { code, message, fieldErrors } }.
          errorMessage = errorData?.error?.message || errorData.message || errorMessage;
          errorCode = errorData?.error?.code;
          fieldErrors = errorData?.error?.fieldErrors;
        } catch {
          errorMessage = errorText || errorMessage;
        }

        // On 401: try refresh token once, then retry request; else clear and redirect to login
        const normalized = (errorMessage || '').toLowerCase();
        const isUnauthorized = response.status === 401 || normalized.includes('invalid or expired token') || normalized.includes('token expired');
        if (isUnauthorized && typeof window !== 'undefined') {
          const { refreshToken } = this.getTokens();
          if (_retryOn401 && refreshToken) {
            try {
              const refreshUrl = `${baseURL}/auth/refresh`;
              const refreshRes = await fetch(refreshUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken }),
              });
              const refreshData = refreshRes.ok ? await refreshRes.json() : null;
              if (refreshData?.success && refreshData?.data?.accessToken) {
                this.setTokensInActiveScope(refreshData.data.accessToken, refreshData.data.refreshToken ?? refreshToken);
                console.log('[ApiService] Token refreshed, retrying request');
                return this.request<T>(endpoint, { ...fetchOptions, _retryOn401: false });
              }
            } catch (e) {
              console.warn('[ApiService] Refresh failed:', e);
            }
          }
          // On QR-guest / transient landings there is no login to send them to — clearing the guest's
          // tokens and redirecting would cascade one stray 401 into a wiped session + a login bounce.
          // Leave the session intact and just surface the error (best-effort callers swallow it).
          if (!this.isTransientPath(window.location.pathname)) {
            try {
              this.clearTokens();
            } catch {}
            if (window.location.pathname !== '/login' && !window.location.pathname.startsWith('/signup')) {
              const currentPath = window.location.pathname + window.location.search;
              if (currentPath) {
                sessionStorage.setItem('redirectAfterLogin', currentPath);
                console.log('[ApiService] Stored redirect path before login:', currentPath);
              }
              window.location.href = '/login';
            }
          }
        }

        const err = new Error(errorMessage) as Error & { code?: string; fieldErrors?: Record<string, string>; status?: number };
        if (errorCode) err.code = errorCode;
        if (fieldErrors) err.fieldErrors = fieldErrors;
        err.status = response.status;
        throw err;
      }

      const data = await response.json();
      console.log('API response received:', data);
      
      // If the response indicates failure (either HTTP error or success: false), throw an error
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }
      
      // Also check if the response has success: false (even with 200 status)
      if (data && data.success === false) {
        throw new Error(data.message || 'Request failed');
      }
      
      return data;
    } catch (error) {
      // Clear timeout if it was set
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      // Check if this is an "already registered" error - log as info instead of error
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isAlreadyRegistered = errorMessage.toLowerCase().includes('already registered');
      
      if (isAlreadyRegistered) {
        console.log('API request info:', errorMessage);
      } else {
        console.error('API request failed:', error);
      }
      
      // Handle abort/timeout errors
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout: The server took too long to respond. Ensure the Central API is running on port 3005 (e.g. open http://localhost:3005/api/v1/check) and try again.');
      }
      
      // Handle network errors (server not reachable, CORS, etc.)
      if (error instanceof TypeError && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
        const baseUrl = this.baseURL;
        throw new Error(`Unable to connect to server at ${baseUrl}. Please check if the backend is running and accessible.`);
      }
      
      throw error;
    }
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    return this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async register(userData: RegisterRequest): Promise<ApiResponse<RegisterResponse>> {
    return this.request<RegisterResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async signupTemporary(data: {
    identifier: string;
    password: string;
    confirmPassword: string;
    identifierType?: 'email' | 'phone';
    firstName: string;
    lastName: string;
  }): Promise<ApiResponse<LoginResponse>> {
    return this.request<LoginResponse>('/auth/signup-temporary', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async refreshToken(refreshToken: string): Promise<ApiResponse<LoginResponse>> {
    return this.request<LoginResponse>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
  }

  async logout(): Promise<ApiResponse> {
    const { accessToken, refreshToken } = this.getTokens();
    
    if (!accessToken) {
      throw new Error('No access token found');
    }

    return this.request('/auth/logout', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ refreshToken }),
    });
  }

  async forgotPassword(email: string): Promise<ApiResponse> {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string): Promise<ApiResponse> {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  async googleAuth(credential: string): Promise<ApiResponse<LoginResponse>> {
    return this.request<LoginResponse>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });
  }

  async firebasePhoneAuth(idToken: string, phoneNumber?: string): Promise<ApiResponse<LoginResponse>> {
    return this.request<LoginResponse>('/auth/firebase-phone', {
      method: 'POST',
      body: JSON.stringify({ 
        idToken,
        ...(phoneNumber && { phoneNumber })
      }),
    });
  }

  async exchangeOAuthCode(code: string, redirectUri?: string): Promise<ApiResponse<LoginResponse>> {
    return this.request<LoginResponse>('/auth/oauth/callback', {
      method: 'POST',
      body: JSON.stringify({ 
        code,
        ...(redirectUri && { redirectUri })
      }),
    });
  }

  // Global platform module flags (public, no auth). Controls whether the SOCIAL and
  // TOURNAMENTS modules are enabled portal-wide. Fail CLOSED: any error -> both disabled.
  async getPlatformFlags(): Promise<{ social: boolean; tournaments: boolean; quick_songs: boolean; marketplace: boolean }> {
    try {
      const res = await this.request<{ social?: boolean; tournaments?: boolean; quick_songs?: boolean; marketplace?: boolean }>(
        '/public/platform-flags',
        { method: 'GET', _retryOn401: false }
      );
      const d = res?.data;
      return { social: !!d?.social, tournaments: !!d?.tournaments, quick_songs: !!d?.quick_songs, marketplace: !!d?.marketplace };
    } catch {
      return { social: false, tournaments: false, quick_songs: false, marketplace: false };
    }
  }

  // ── Marketplace (mediator groups) ──────────────────────────────────────────
  async marketListGroups(search?: string): Promise<any> { return this.request<any>(`/customers/marketplace/groups${search ? `?search=${encodeURIComponent(search)}` : ''}`, { method: 'GET' }); }
  async marketMyGroups(): Promise<any> { return this.request<any>('/customers/marketplace/my-groups', { method: 'GET' }); }
  async marketCreateGroup(body: { title: string; commodity?: string; description?: string }): Promise<any> { return this.request<any>('/customers/marketplace/groups', { method: 'POST', body: JSON.stringify(body) }); }
  async marketGetGroup(groupId: string): Promise<any> { return this.request<any>(`/customers/marketplace/groups/${groupId}`, { method: 'GET' }); }
  async marketRequestJoin(groupId: string, role: 'seller' | 'buyer'): Promise<any> { return this.request<any>(`/customers/marketplace/groups/${groupId}/join`, { method: 'POST', body: JSON.stringify({ role }) }); }
  async marketListRequests(groupId: string): Promise<any> { return this.request<any>(`/customers/marketplace/groups/${groupId}/requests`, { method: 'GET' }); }
  async marketApprove(groupId: string, memberId: string): Promise<any> { return this.request<any>(`/customers/marketplace/groups/${groupId}/requests/${memberId}/approve`, { method: 'POST' }); }
  async marketReject(groupId: string, memberId: string): Promise<any> { return this.request<any>(`/customers/marketplace/groups/${groupId}/requests/${memberId}/reject`, { method: 'POST' }); }
  async marketReveal(groupId: string): Promise<any> { return this.request<any>(`/customers/marketplace/groups/${groupId}/reveal`, { method: 'GET' }); }
  async marketGetMessages(groupId: string): Promise<any> { return this.request<any>(`/customers/marketplace/groups/${groupId}/messages`, { method: 'GET' }); }
  async marketSendMessage(groupId: string, text: string): Promise<any> { return this.request<any>(`/customers/marketplace/groups/${groupId}/messages`, { method: 'POST', body: JSON.stringify({ text }) }); }
  async marketListOffers(groupId: string): Promise<any> { return this.request<any>(`/customers/marketplace/groups/${groupId}/offers`, { method: 'GET' }); }
  async marketPostOffer(groupId: string, body: { commodity?: string; quantity?: number; unit?: string; price?: number; currency?: string; note?: string }): Promise<any> { return this.request<any>(`/customers/marketplace/groups/${groupId}/offers`, { method: 'POST', body: JSON.stringify(body) }); }
  async marketWithdrawOffer(groupId: string, offerId: string): Promise<any> { return this.request<any>(`/customers/marketplace/groups/${groupId}/offers/${offerId}/withdraw`, { method: 'POST' }); }
  async marketListDeals(groupId: string): Promise<any> { return this.request<any>(`/customers/marketplace/groups/${groupId}/deals`, { method: 'GET' }); }
  async marketRecordDeal(groupId: string, body: { sellerMemberId: string; buyerMemberId: string; commodity?: string; quantity?: number; price?: number; commissionAmount?: number; commissionPercent?: number; note?: string }): Promise<any> { return this.request<any>(`/customers/marketplace/groups/${groupId}/deals`, { method: 'POST', body: JSON.stringify(body) }); }
  async marketRemoveMember(groupId: string, memberId: string): Promise<any> { return this.request<any>(`/customers/marketplace/groups/${groupId}/members/${memberId}/remove`, { method: 'POST' }); }

  // Public endpoints (require auth token)
  async getActiveOrganizers(): Promise<ApiResponse<any[]>> {
    return this.request<any[]>('/public/organizers/active', {
      method: 'GET'
    });
  }

  // --- QR "scan to call staff" (truly unauthenticated: bare fetch so a missing/expired token
  //     can't trigger the 401 -> /login redirect that request() would fire) ---

  /** Landing-page info for a scanned table QR: { tableLabel, branchName, available }. */
  async getQrTable(token: string): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/public/qr/${encodeURIComponent(token)}`, { method: 'GET' });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data; // { success, data: { tableId, tableLabel, branchId, branchName, available } }
  }

  /** Provision a guest customer for a scanned table QR and return a session + the walk-in booking. */
  async createQrSession(token: string, body: { deviceId?: string; name?: string; sessionId?: string; rememberToken?: string }): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/public/qr/${encodeURIComponent(token)}/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    });
    const data = await res.json();
    if (!res.ok) {
      const err: any = new Error(data?.message || `HTTP ${res.status}`);
      err.code = data?.error;   // e.g. 'QR_SESSION_ENDED', 'QR_UNAVAILABLE'
      err.status = res.status;
      throw err;
    }
    return data; // { success, data: { accessToken, refreshToken, bookingId, sessionId, reused, ... } }
  }

  /** Salon branch QR — venue + the stylist list (with free/busy) for the picker. */
  async getBranchStylists(token: string): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/public/branch/${encodeURIComponent(token)}`, { method: 'GET' });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data; // { success, data: { branchId, branchName, stylists: [{id, displayName, photoUrl, specialties, busy}] } }
  }

  /** Provision a guest bound to a chosen stylist and return a session + walk-in booking. */
  async createSalonSession(token: string, body: { stylistId: string; deviceId?: string; name?: string; sessionId?: string }): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/public/branch/${encodeURIComponent(token)}/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    });
    const data = await res.json();
    if (!res.ok) {
      const err: any = new Error(data?.message || `HTTP ${res.status}`);
      err.code = data?.error;
      err.status = res.status;
      throw err;
    }
    return data; // { success, data: { accessToken, refreshToken, bookingId, sessionId, stylistId, stylistName, ... } }
  }

  /** Salon appointment — free time slots for a stylist on a date. */
  async getSalonAvailability(token: string, stylistId: string, date: string, durationMinutes: number): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const qs = new URLSearchParams({ stylistId, date, durationMinutes: String(durationMinutes) }).toString();
    const res = await fetch(`${baseURL}/public/branch/${encodeURIComponent(token)}/availability?${qs}`, { method: 'GET' });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data; // { success, data: { date, slots: ["09:00", ...] } }
  }

  /** Salon appointment — request a future slot with a chosen stylist + service. */
  async createSalonAppointment(token: string, body: { stylistId: string; serviceItemId?: string; date: string; time: string; name?: string; phone?: string }): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/public/branch/${encodeURIComponent(token)}/appointment`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data; // { success, data: { bookingId, date, time, stylistName, serviceName, durationMinutes } }
  }

  /** Public booking widget — the salon's service menu + professionals for the shareable /book/:token page. */
  async getPublicBooking(token: string): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/public/book/${encodeURIComponent(token)}`, { method: 'GET' });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data; // { success, data: { branchName, currency, themeId, services:[...], staff:[...] } }
  }

  /** Public booking widget — free start-times for a professional on a date (service sets the duration). */
  async getPublicBookingSlots(token: string, staffId: string, date: string, serviceId?: string): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const qs = new URLSearchParams({ staffId, date, ...(serviceId ? { serviceId } : {}) }).toString();
    const res = await fetch(`${baseURL}/public/book/${encodeURIComponent(token)}/slots?${qs}`, { method: 'GET' });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data; // { success, data: { date, slots: ["09:00", ...] } }
  }

  /** Public booking widget — request an appointment (lands in the owner's diary as `requested`). */
  async createPublicBooking(token: string, body: { serviceId?: string; staffId?: string; date: string; time: string; name: string; phone?: string; notes?: string }): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/public/book/${encodeURIComponent(token)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data; // { success, data: { id, date, time, serviceName, staffName, durationMinutes } }
  }

  /** Public booking widget — join the waitlist when a day is full. */
  async joinPublicWaitlist(token: string, body: { serviceId?: string; staffId?: string; date?: string; timePref?: string; name: string; phone?: string; note?: string }): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/public/book/${encodeURIComponent(token)}/waitlist`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data;
  }

  /** In-app appointments — join the waitlist for a full day (attached to my account). */
  async joinBranchWaitlist(branchId: string, body: { serviceId?: string; staffId?: string; date?: string; timePref?: string; name: string; phone?: string; note?: string }): Promise<any> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/appointments/waitlist`, {
      method: 'POST', body: JSON.stringify(body || {}),
    });
  }

  /** Post-visit review — public info for the review page (what the guest is rating). */
  async getPublicReview(refType: string, refId: string): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/public/review/${encodeURIComponent(refType)}/${encodeURIComponent(refId)}`, { method: 'GET' });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data;
  }
  /** Post-visit review — submit a ★ rating + comment. */
  async submitPublicReview(refType: string, refId: string, body: { rating: number; comment?: string; name?: string }): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/public/review/${encodeURIComponent(refType)}/${encodeURIComponent(refId)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data;
  }

  // ── Privacy & data rights (GDPR / PDPL / DPDP) ──
  /** Download everything we hold about the logged-in customer (JSON object). */
  async exportMyData(): Promise<any> {
    return this.request<any>('/customers/me/data-export');
  }
  /** Permanently delete the account: anonymise personal data + deactivate login. */
  async deleteMyAccount(): Promise<any> {
    return this.request<any>('/customers/me/delete-account', {
      method: 'POST', body: JSON.stringify({ confirm: true }),
    });
  }
  /** Log a consent decision (cookie acceptance, policy acceptance). Best-effort. */
  async recordConsent(consentType: string, granted = true, version = '1'): Promise<any> {
    return this.request<any>('/customers/me/consent', {
      method: 'POST', body: JSON.stringify({ consentType, granted, version }),
    });
  }
  /** Email ownership verification — send the confirmation link to my email. */
  async sendEmailVerification(): Promise<any> {
    return this.request<any>('/customers/me/verify-email/send', { method: 'POST' });
  }
  /** Consume an email-verification token (public — from the /verify-email link). */
  async verifyEmailToken(token: string): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/auth/verify-email`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data as any)?.message || `HTTP ${res.status}`);
    return data;
  }

  /** Hotel room QR — public info for the landing page. */
  async getRoomQr(token: string): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/public/r/${encodeURIComponent(token)}`, { method: 'GET' });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data; // { success, data: { roomId, roomLabel, branchId, branchName, organizerName, hasActiveStay } }
  }

  /** Attach a walk-in guest to the room's active stay and return a session. */
  async createRoomQrSession(token: string, body: { deviceId?: string; name?: string }): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/public/r/${encodeURIComponent(token)}/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data; // { success, data: { accessToken, refreshToken, bookingId, branchId, roomLabel } }
  }

  /** QR guest's optional display name (authenticated — uses the session set after provisioning). */
  async getQrProfile(): Promise<ApiResponse<{ displayName: string | null }>> {
    return this.request<{ displayName: string | null }>('/customers/qr-profile', { method: 'GET' });
  }

  /** Recent staff broadcasts for a branch (venue-wide announcements pushed to all tables). */
  async getBranchBroadcasts(branchId: string): Promise<Array<{ id: string; message: string; createdByName: string | null; createdAt: string }>> {
    if (!branchId) return [];
    try {
      const res = await this.request<Array<{ id: string; message: string; createdByName: string | null; createdAt: string }>>(`/customers/branches/${branchId}/broadcasts`, { method: 'GET' });
      return res.data || [];
    } catch {
      return [];
    }
  }

  /** Active, AI-public venue events/offers for the hero carousel. Best-effort (empty on failure). */
  async getBranchHighlights(branchId: string): Promise<Array<{ id: string; title: string; category: string; when: string | null; description: string | null; startsAt?: string | null; endsAt?: string | null; allDay?: boolean }>> {
    if (!branchId) return [];
    try {
      const res = await this.request<Array<{ id: string; title: string; category: string; when: string | null; description: string | null; startsAt?: string | null; endsAt?: string | null; allDay?: boolean }>>(`/customers/branches/${branchId}/highlights`, { method: 'GET' });
      return res.data || [];
    } catch {
      return [];
    }
  }

  /** Whether AI translation is available for THIS branch (global flag ∧ key ∧ admin-allowed ∧ organizer-enabled). Fail-closed. */
  async getTranslationAvailableForBranch(branchId: string): Promise<boolean> {
    if (!branchId) return false;
    try {
      const res = await this.request<{ available: boolean }>(`/customers/branches/${branchId}/ai/translation-available`, { method: 'GET' });
      return !!res.data?.available;
    } catch {
      return false;
    }
  }

  /** AI-translate a chat message into `to` (a language name or code). Passes branchId for per-branch gating. */
  async translateText(text: string, to: string, branchId?: string, from?: string): Promise<string> {
    const res = await this.request<{ translated: string }>('/customers/ai/translate', {
      method: 'POST',
      body: JSON.stringify({ text, to, branchId, ...(from ? { from } : {}) }),
    });
    return res.data?.translated ?? text;
  }

  /** Group tab (shared cart) — current tab for this guest, or null. */
  async getGroupTab(bookingId: string): Promise<ApiResponse<any | null>> {
    return this.request<any>(`/customers/group-tab?bookingId=${encodeURIComponent(bookingId)}`, { method: 'GET' });
  }
  async startGroupTab(bookingId: string): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/group-tab/start', { method: 'POST', body: JSON.stringify({ bookingId }) });
  }
  async joinGroupTab(bookingId: string, code: string): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/group-tab/join', { method: 'POST', body: JSON.stringify({ bookingId, code }) });
  }
  async leaveGroupTab(bookingId: string): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/group-tab/leave', { method: 'POST', body: JSON.stringify({ bookingId }) });
  }
  async getGroupSplit(bookingId: string): Promise<ApiResponse<any | null>> {
    return this.request<any>(`/customers/group-tab/split?bookingId=${encodeURIComponent(bookingId)}`, { method: 'GET' });
  }
  async payGroupShare(bookingId: string, amount: number, method: string): Promise<ApiResponse<{ paid: number; remaining: number; settled: boolean }>> {
    return this.request<any>('/customers/group-tab/pay', { method: 'POST', body: JSON.stringify({ bookingId, amount, method }) });
  }

  /** AI waiter — ask a menu question; answer is grounded in the branch's menu. Same gate as translation. */
  async askAiWaiter(branchId: string, question: string): Promise<string> {
    const res = await this.request<{ answer: string }>('/customers/ai/waiter', {
      method: 'POST',
      body: JSON.stringify({ branchId, question }),
    });
    return res.data?.answer ?? '';
  }

  /** Set/update the QR guest's display name (blank clears it → "Guest"). */
  async updateQrProfile(displayName: string): Promise<ApiResponse<{ displayName: string }>> {
    return this.request<{ displayName: string }>('/customers/qr-profile', {
      method: 'PATCH',
      body: JSON.stringify({ displayName }),
    });
  }

  async getOrganizerDetails(organizerId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/public/organizers/${encodeURIComponent(organizerId)}`, {
      method: 'GET'
    });
  }

  async getOrganizerTournaments(organizerId: string, branchId?: string): Promise<ApiResponse<any[]>> {
    let url = `/public/organizers/${encodeURIComponent(organizerId)}/tournaments`;
    
    // Add branchId query parameter if provided
    if (branchId) {
      url += `?branchId=${encodeURIComponent(branchId)}`;
    }
    
    return this.request<any[]>(url, {
      method: 'GET'
    });
  }

  // Customer endpoints - Get branches for an organizer
  async getOrganizerBranches(organizerId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/organizers/${encodeURIComponent(organizerId)}/branches`, {
      method: 'GET'
    });
  }

  // Customer endpoints - Get branch details with areas and tables
  async getBranchDetails(branchId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/details`, {
      method: 'GET'
    });
  }

  // Customer endpoints - Which service segments (features) this branch offers → drives nav
  async getBranchFeatures(branchId: string): Promise<any> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/features`, {
      method: 'GET'
    });
  }

  /** Appointments (salon/spa/barber/clinic) — logged-in customer: the branch's service menu + professionals. */
  async getBranchAppointmentInfo(branchId: string): Promise<any> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/appointment-info`, { method: 'GET' });
  }
  /** Appointments — free time slots for a professional on a date (service sets the duration). */
  async getBranchAppointmentSlots(branchId: string, staffId: string, date: string, serviceId?: string): Promise<any> {
    const qs = new URLSearchParams({ staffId, date, ...(serviceId ? { serviceId } : {}) }).toString();
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/appointment-slots?${qs}`, { method: 'GET' });
  }
  /** Appointments — request an appointment; lands in the owner's diary as `requested`, attached to my account. */
  async createBranchAppointment(branchId: string, body: { serviceId?: string; staffId?: string; date: string; time: string; name: string; phone?: string; notes?: string }): Promise<any> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/appointments`, {
      method: 'POST', body: JSON.stringify(body || {}),
    });
  }

  /** Offers running at this branch right now — display only; the money is computed server-side. */
  async getBranchOffers(branchId: string): Promise<any> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/offers`, { method: 'GET' });
  }

  // ---- Referral (invite a contact) ----
  /** My personal referral code (auto-created on first call). Registered customers only. */
  async getReferralCode(): Promise<ApiResponse<{ code: string }>> {
    return this.request<{ code: string }>(`/customers/referral/code`, { method: 'GET' });
  }
  /** Record that I was referred to a branch via a code (called when a contact lands + is registered). */
  async recordReferral(code: string, branchId: string): Promise<ApiResponse<{ ok: boolean; reason?: string }>> {
    return this.request<{ ok: boolean; reason?: string }>(`/customers/referral/record`, { method: 'POST', body: JSON.stringify({ code, branchId }) });
  }
  /** My active reward vouchers (optionally only those usable at a branch). */
  async getReferralVouchers(branchId?: string): Promise<ApiResponse<any[]>> {
    const q = branchId ? `?branchId=${encodeURIComponent(branchId)}` : '';
    return this.request<any[]>(`/customers/referral/vouchers${q}`, { method: 'GET' });
  }
  /** My wallet balance at a branch. */
  async getWalletBalance(branchId: string): Promise<ApiResponse<{ balance: number; currency: string | null }>> {
    return this.request<any>(`/customers/wallet/balance?branchId=${encodeURIComponent(branchId)}`, { method: 'GET' });
  }
  /** My wallet transaction history at a branch. */
  async getWalletTransactions(branchId: string): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/customers/wallet/transactions?branchId=${encodeURIComponent(branchId)}`, { method: 'GET' });
  }
  /** Redeem a gift card code into my wallet at a branch. */
  async redeemGiftCard(code: string, branchId: string): Promise<ApiResponse<{ credited: number; balance: number }>> {
    return this.request<any>(`/customers/wallet/redeem-gift`, { method: 'POST', body: JSON.stringify({ code, branchId }) });
  }
  /** Guest self-declares they're of legal age for a booking (age-check soft gate). */
  async declareAge(bookingId: string): Promise<ApiResponse<{ ok: boolean }>> {
    return this.request<{ ok: boolean }>(`/customers/bookings/${encodeURIComponent(bookingId)}/age-declare`, { method: 'POST' });
  }
  /** My loyalty stamp-card progress at a branch (null when the branch has no active card). */
  async getLoyaltyCard(branchId: string): Promise<ApiResponse<{ stamps: number; goal: number; rewardLabel: string | null; active: boolean } | null>> {
    return this.request<any>(`/customers/loyalty/card?branchId=${encodeURIComponent(branchId)}`, { method: 'GET' });
  }

  // ---- Concierge ("What do you need?") ----
  async conciergeAsk(bookingId: string, message: string, history?: Array<{ role: string; text: string }>): Promise<ApiResponse<{ reply: string; action?: { capability: string; kind: 'execute' | 'navigate'; label: string; args: any } }>> {
    return this.request<any>(`/customers/concierge`, { method: 'POST', body: JSON.stringify({ bookingId, message, history: history || [] }) });
  }
  async conciergeExecute(bookingId: string, capability: string, args: any): Promise<ApiResponse<{ ok: boolean; message: string }>> {
    return this.request<any>(`/customers/concierge/execute`, { method: 'POST', body: JSON.stringify({ bookingId, capability, args }) });
  }
  /** Active AI concierge persona for this branch (name/tone + voice mode), or null. */
  async getBranchAiAgent(branchId: string): Promise<{ name: string | null; tone: string | null; voiceEnabled: boolean; voice: string | null } | null> {
    if (!branchId) return null;
    try {
      const res = await this.request<any>(`/customers/branches/${branchId}/ai-agent`, { method: 'GET' });
      return (res as any)?.data || null;
    } catch { return null; }
  }

  // ---- Same-table Ludo (backend-authoritative; state also arrives via Ably table_game:{tableId}) ----
  async ludoCurrent(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/ludo?bookingId=${encodeURIComponent(bookingId)}`, { method: 'GET' }))?.data ?? null; }
  async ludoStart(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/ludo/start`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }
  async ludoJoin(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/ludo/join`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }
  async ludoBegin(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/ludo/begin`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }
  async ludoRoll(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/ludo/roll`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }
  async ludoMove(bookingId: string, tokenIndex: number): Promise<any> { return (await this.request<any>(`/customers/table-game/ludo/move`, { method: 'POST', body: JSON.stringify({ bookingId, tokenIndex }) }))?.data ?? null; }
  async ludoTurnTime(bookingId: string, seconds: number): Promise<any> { return (await this.request<any>(`/customers/table-game/ludo/turn-time`, { method: 'POST', body: JSON.stringify({ bookingId, seconds }) }))?.data ?? null; }
  async ludoLeave(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/ludo/leave`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }
  async ludoKick(bookingId: string, targetUserId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/ludo/kick`, { method: 'POST', body: JSON.stringify({ bookingId, targetUserId }) }))?.data ?? null; }
  async ludoReset(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/ludo/reset`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }

  // ---- Same-table Chess (backend-authoritative; state also arrives via Ably table_chess:{tableId}) ----
  async chessCurrent(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/chess?bookingId=${encodeURIComponent(bookingId)}`, { method: 'GET' }))?.data ?? null; }
  async chessStart(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/start`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }
  async chessRequestPlay(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/request`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }
  async chessAccept(bookingId: string, targetUserId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/accept`, { method: 'POST', body: JSON.stringify({ bookingId, targetUserId }) }))?.data ?? null; }
  async chessDecline(bookingId: string, targetUserId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/decline`, { method: 'POST', body: JSON.stringify({ bookingId, targetUserId }) }))?.data ?? null; }
  async chessSetSide(bookingId: string, side: 'w' | 'b'): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/side`, { method: 'POST', body: JSON.stringify({ bookingId, side }) }))?.data ?? null; }
  async chessBegin(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/begin`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }
  async chessMove(bookingId: string, from: number, to: number, promo?: string): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/move`, { method: 'POST', body: JSON.stringify({ bookingId, from, to, promo }) }))?.data ?? null; }
  async chessResign(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/resign`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }
  async chessOfferDraw(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/draw-offer`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }
  async chessAcceptDraw(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/draw-accept`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }
  async chessTurnTime(bookingId: string, seconds: number): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/turn-time`, { method: 'POST', body: JSON.stringify({ bookingId, seconds }) }))?.data ?? null; }
  async chessSetClock(bookingId: string, minutes: number, incrementSeconds: number): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/clock`, { method: 'POST', body: JSON.stringify({ bookingId, minutes, incrementSeconds }) }))?.data ?? null; }
  async chessLeave(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/leave`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }
  async chessKick(bookingId: string, targetUserId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/kick`, { method: 'POST', body: JSON.stringify({ bookingId, targetUserId }) }))?.data ?? null; }
  async chessReset(bookingId: string): Promise<any> { return (await this.request<any>(`/customers/table-game/chess/reset`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }

  // ---- In-game chat (shared by Ludo + Chess; relays over the game's Ably channel) ----
  async gameChatHistory(bookingId: string, game: 'ludo' | 'chess'): Promise<any[]> { return (await this.request<any>(`/customers/table-game/chat?bookingId=${encodeURIComponent(bookingId)}&game=${game}`, { method: 'GET' }))?.data ?? []; }
  async gameChatSend(bookingId: string, game: 'ludo' | 'chess', text: string): Promise<any> { return (await this.request<any>(`/customers/table-game/chat`, { method: 'POST', body: JSON.stringify({ bookingId, game, text }) }))?.data ?? null; }

  // ---- Bottle keep (a customer's own kept bottles) ----
  async getMyKeptBottles(bookingId: string): Promise<any[]> { try { return (await this.request<any>(`/customers/kept-bottles?bookingId=${encodeURIComponent(bookingId)}`, { method: 'GET' }))?.data ?? []; } catch { return []; } }
  async requestKeptBottle(bookingId: string, id: string): Promise<any> { return (await this.request<any>(`/customers/kept-bottles/${encodeURIComponent(id)}/request`, { method: 'POST', body: JSON.stringify({ bookingId }) }))?.data ?? null; }

  /** Proactive personalised opener for a recognised regular (null for anonymous guests). */
  async conciergeGreeting(bookingId: string): Promise<ApiResponse<{ reply: string; action?: { capability: string; kind: string; label: string; args: any }; feedbackItem?: { itemName: string; stockItemId: string | null } | null } | null>> {
    return this.request<any>(`/customers/concierge/greeting?bookingId=${encodeURIComponent(bookingId)}`, { method: 'GET' });
  }
  /** Thumbs up/down on an item ("hope you enjoyed it?"). */
  async conciergeFeedback(bookingId: string, itemName: string, stockItemId: string | null, sentiment: 'up' | 'down'): Promise<ApiResponse<{ ok: boolean; message: string }>> {
    return this.request<any>(`/customers/concierge/feedback`, { method: 'POST', body: JSON.stringify({ bookingId, itemName, stockItemId, sentiment }) });
  }
  /** Server-side concierge chat history (persists across refresh, until checkout). */
  async conciergeHistory(bookingId: string): Promise<ApiResponse<Array<{ role: string; text: string }>>> {
    return this.request<any>(`/customers/concierge/history?bookingId=${encodeURIComponent(bookingId)}`, { method: 'GET' });
  }
  async conciergeAppendMessage(bookingId: string, role: string, text: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/concierge/history`, { method: 'POST', body: JSON.stringify({ bookingId, role, text }) });
  }
  async conciergeClearHistory(bookingId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/concierge/history?bookingId=${encodeURIComponent(bookingId)}`, { method: 'DELETE' });
  }

  // ---- Table Chat (guest-to-guest, same table) ----
  async tableChatRoster(bookingId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/table-chat/roster?bookingId=${encodeURIComponent(bookingId)}`, { method: 'GET' });
  }
  async tableChatGroup(bookingId: string): Promise<ApiResponse<any[]>> {
    return this.request<any>(`/customers/table-chat/group?bookingId=${encodeURIComponent(bookingId)}`, { method: 'GET' });
  }
  async tableChatPostGroup(bookingId: string, body: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/table-chat/group`, { method: 'POST', body: JSON.stringify({ bookingId, body }) });
  }
  async tableChatShareTab(bookingId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/table-chat/group/share-tab`, { method: 'POST', body: JSON.stringify({ bookingId }) });
  }
  async tableChatMarkGroupRead(bookingId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/table-chat/group/read`, { method: 'POST', body: JSON.stringify({ bookingId }) });
  }
  async tableChatOpenDm(bookingId: string, otherBookingId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/table-chat/dm/open`, { method: 'POST', body: JSON.stringify({ bookingId, otherBookingId }) });
  }
  async tableChatDmList(bookingId: string, threadId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/table-chat/dm/${encodeURIComponent(threadId)}?bookingId=${encodeURIComponent(bookingId)}`, { method: 'GET' });
  }
  async tableChatDmSend(bookingId: string, threadId: string, body: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/table-chat/dm/${encodeURIComponent(threadId)}`, { method: 'POST', body: JSON.stringify({ bookingId, body }) });
  }
  async tableChatDmRead(bookingId: string, threadId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/table-chat/dm/${encodeURIComponent(threadId)}/read`, { method: 'POST', body: JSON.stringify({ bookingId }) });
  }
  async tableChatDmBlock(bookingId: string, threadId: string, blocked: boolean): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/table-chat/dm/${encodeURIComponent(threadId)}/block`, { method: 'POST', body: JSON.stringify({ bookingId, blocked }) });
  }
  async tableChatDmReport(bookingId: string, threadId: string, reason?: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/table-chat/dm/${encodeURIComponent(threadId)}/report`, { method: 'POST', body: JSON.stringify({ bookingId, reason }) });
  }

  // Customer endpoints - Global browse-by-category (business types with active branches)
  async getBranchCategories(): Promise<any> {
    return this.request<any>(`/customers/branch-categories`, { method: 'GET' });
  }

  async getBranchesByCategory(code: string): Promise<any> {
    return this.request<any>(`/customers/branch-categories/${encodeURIComponent(code)}/branches`, { method: 'GET' });
  }

  // Customer endpoints - Get the branch's menu photos (viewed at a table; QR guests included)
  async getBranchMenuImages(branchId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/menu-images`, {
      method: 'GET'
    });
  }

  /** Public "About Us" for a branch — description, address, contact, hours. Works for QR guests and
   *  registered customers (no auth needed). */
  async getBranchAbout(branchId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/public/branches/${encodeURIComponent(branchId)}/about`, {
      method: 'GET'
    });
  }

  // ---- Branch Central Gallery (members = registered customers who visited) ----
  /** Published gallery posts for a branch. 403 (NOT_A_MEMBER) for QR guests / non-visitors. */
  async getBranchGallery(branchId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/gallery`, { method: 'GET' });
  }
  async getGalleryComments(postId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/gallery/${encodeURIComponent(postId)}/comments`, { method: 'GET' });
  }
  async addGalleryComment(postId: string, text: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/gallery/${encodeURIComponent(postId)}/comments`, { method: 'POST', body: JSON.stringify({ text }) });
  }
  /** The featured guest approves/declines a post that features them. */
  async setGalleryConsent(postId: string, approve: boolean): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/gallery/${encodeURIComponent(postId)}/consent`, { method: 'POST', body: JSON.stringify({ approve }) });
  }
  /** Posts awaiting the logged-in customer's consent ("you appear in these"). */
  async getGalleryAwaitingConsent(): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/gallery/awaiting-consent', { method: 'GET' });
  }
  /** A member submits their own post (photo/video via file, or a link) → pending staff review. */
  async submitGalleryPost(branchId: string, opts: { file?: File | null; caption?: string; linkUrl?: string }): Promise<any> {
    const formData = new FormData();
    if (opts.file) formData.append('mediaFile', opts.file);
    if (opts.caption) formData.append('caption', opts.caption);
    if (opts.linkUrl) formData.append('linkUrl', opts.linkUrl);
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const url = `${baseURL}/customers/branches/${encodeURIComponent(branchId)}/gallery`;
    const { accessToken } = this.getTokens();
    const headers: Record<string, string> = {};
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    const response = await fetch(url, { method: 'POST', headers, body: formData });
    const json = await response.json().catch(() => ({}));
    if (!response.ok || !json.success) throw new Error(json.message || `HTTP ${response.status}`);
    return json.post;
  }

  // ---- Bottle service / VIP tables ----
  async getVipPackages(branchId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/vip/packages`, { method: 'GET' });
  }
  async createVipReservation(branchId: string, body: { packageId?: string; reservationDate: string; arrivalTime?: string; partySize?: number; guestName?: string; guestPhone?: string; notes?: string }): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/vip/reservations`, { method: 'POST', body: JSON.stringify(body) });
  }
  async getMyVipReservations(): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/vip/reservations', { method: 'GET' });
  }

  // ---- Hotel rooms (book a stay) ----
  async getRoomAvailability(branchId: string, checkIn: string, checkOut: string, guests = 1): Promise<any> {
    const qs = new URLSearchParams({ checkIn, checkOut, guests: String(guests) });
    return this.request<any>(`/customers/rooms/${encodeURIComponent(branchId)}/availability?${qs.toString()}`, { method: 'GET' });
  }
  async createRoomBooking(payload: { branchId: string; roomTypeId: string; checkInDate: string; checkOutDate: string; numberOfGuests?: number; specialRequests?: string }): Promise<any> {
    return this.request<any>(`/customers/rooms/bookings`, { method: 'POST', body: JSON.stringify(payload) });
  }
  async getMyRoomBookings(): Promise<any> {
    return this.request<any>(`/customers/rooms/bookings`, { method: 'GET' });
  }
  async cancelRoomBooking(bookingId: string): Promise<any> {
    return this.request<any>(`/customers/rooms/bookings/${encodeURIComponent(bookingId)}/cancel`, { method: 'POST' });
  }
  // In-room services during a stay
  async createRoomRequest(bookingId: string, payload: { requestType: string; category?: string; note?: string; items?: Array<{ stockItemId?: string | null; itemName: string; itemImage?: string | null; unitPrice?: number | null; quantity: number }> }): Promise<any> {
    return this.request<any>(`/customers/rooms/bookings/${encodeURIComponent(bookingId)}/requests`, { method: 'POST', body: JSON.stringify(payload) });
  }
  // Menu items available for a given scope ('table' | 'room'), grouped by category
  async getBranchMenu(branchId: string, scope: 'table' | 'room' = 'table'): Promise<any> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/stock-items?scope=${scope}`, { method: 'GET' });
  }
  // --- Send a drink / toast to another table ---
  async getGiftTables(branchId: string, bookingId: string): Promise<{ bookingId: string; name: string; tableLabel: string }[]> {
    const res = await this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/gift-tables?bookingId=${encodeURIComponent(bookingId)}`, { method: 'GET' });
    return (res?.data ?? []) as { bookingId: string; name: string; tableLabel: string }[];
  }
  async sendGift(payload: { senderBookingId: string; recipientBookingId: string; kind: 'toast' | 'drink'; stockItemId?: string | null; message?: string | null; anonymous?: boolean }): Promise<any> {
    return (await this.request<any>(`/customers/gifts`, { method: 'POST', body: JSON.stringify(payload) }))?.data ?? null;
  }
  async respondGift(giftId: string, action: 'accept' | 'cheers_back' | 'maybe_later'): Promise<any> {
    return (await this.request<any>(`/customers/gifts/${encodeURIComponent(giftId)}/respond`, { method: 'POST', body: JSON.stringify({ action }) }))?.data ?? null;
  }
  async getMyGifts(bookingId: string): Promise<{ incoming: any[]; recent: any[] }> {
    const res = await this.request<any>(`/customers/gifts?bookingId=${encodeURIComponent(bookingId)}`, { method: 'GET' });
    return (res?.data ?? { incoming: [], recent: [] }) as { incoming: any[]; recent: any[] };
  }
  async setGiftOptOut(bookingId: string, optOut: boolean): Promise<any> {
    return (await this.request<any>(`/customers/gifts/opt-out`, { method: 'POST', body: JSON.stringify({ bookingId, optOut }) }))?.data ?? null;
  }

  async listRoomRequests(bookingId: string): Promise<any> {
    return this.request<any>(`/customers/rooms/bookings/${encodeURIComponent(bookingId)}/requests`, { method: 'GET' });
  }
  async cancelRoomRequest(requestId: string): Promise<any> {
    return this.request<any>(`/customers/rooms/requests/${encodeURIComponent(requestId)}/cancel`, { method: 'POST' });
  }
  async listRoomChat(bookingId: string): Promise<any> {
    return this.request<any>(`/customers/rooms/bookings/${encodeURIComponent(bookingId)}/chat`, { method: 'GET' });
  }
  async markRoomChatRead(bookingId: string): Promise<any> {
    return this.request<any>(`/customers/rooms/bookings/${encodeURIComponent(bookingId)}/read`, { method: 'POST' });
  }
  async sendRoomChat(bookingId: string, text: string): Promise<any> {
    return this.request<any>(`/customers/rooms/bookings/${encodeURIComponent(bookingId)}/chat`, { method: 'POST', body: JSON.stringify({ text }) });
  }
  /** Booker generates the group QR for friends. Returns { shareToken, url, qrPng }. */
  async generateRoomShareToken(bookingId: string): Promise<any> {
    return this.request<any>(`/customers/rooms/bookings/${encodeURIComponent(bookingId)}/share`, { method: 'POST' });
  }
  async getRoomCallReasons(branchId: string): Promise<any> {
    return this.request<any>(`/customers/rooms/${encodeURIComponent(branchId)}/call-reasons`, { method: 'GET' });
  }
  /** The branch's configured ACTIVE table-call reasons (ordered). Falls back to hardcoded sets if empty. */
  async getTableCallReasons(branchId: string): Promise<any> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/call-reasons`, { method: 'GET' });
  }
  /** Public booker-group-QR: info + join session (friend = call staff + chat only). */
  async getRoomShare(token: string): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/public/rg/${encodeURIComponent(token)}`, { method: 'GET' });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data;
  }
  async createRoomShareSession(token: string, body: { deviceId?: string; name?: string }): Promise<any> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const res = await fetch(`${baseURL}/public/rg/${encodeURIComponent(token)}/session`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data;
  }

  // Customer endpoints - Get table bookings for a specific table
  async getTableBookings(tableId: string): Promise<ApiResponse<{ tableId: string; bookings: any[]; count: number }>> {
    return this.request<{ tableId: string; bookings: any[]; count: number }>(`/customers/tables/${encodeURIComponent(tableId)}/bookings`, {
      method: 'GET'
    });
  }

  // Customer endpoints - Create a dining table booking
  // Note: These are dining/drinking tables where customers sit, eat, drink, chat, order food
  // This is separate from pool/billiards tables used for tournaments
  async createBooking(bookingData: {
    tableId: string;
    branchId: string;
    bookingDate?: string;
    startTime?: string;
    durationMinutes?: number;
    numberOfCustomers?: number;
    specialRequests?: string;
    chairNumber?: number;
    mainBookingId?: string;
    allowJoin?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/bookings', {
      method: 'POST',
      body: JSON.stringify(bookingData)
    });
  }

  /** Instant walk-in: seat + check the customer in immediately at an AVAILABLE table.
   *  409 if they already have an active booking at this branch; 400 if the table isn't available. */
  async sitNow(tableId: string): Promise<ApiResponse<{ bookingId: string; tableId: string; tableLabel?: string; tableNumber?: number; branchId: string; branchName?: string; status: string }>> {
    return this.request<any>(`/customers/tables/${encodeURIComponent(tableId)}/sit-now`, {
      method: 'POST'
    });
  }

  /** "I'm here" fast path: send a booking request for NOW in one tap (no date/time picking).
   *  Lands as 'requested', so staff still approve + check in — this is NOT a self-check-in.
   *  409 if the customer already has an active booking at this branch. */
  async imHereNow(tableId: string, numberOfCustomers?: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/tables/${encodeURIComponent(tableId)}/im-here`, {
      method: 'POST',
      body: JSON.stringify({ numberOfCustomers: numberOfCustomers ?? 1 })
    });
  }

  /**
   * "Claim my session": carry an anonymous QR guest session (live booking, orders, open tab, chat,
   * kept bottles) onto the now-logged-in registered account. Call this AFTER logging in — it is
   * authed with the registered token; `guestToken` is the guest token captured (via
   * {@link getGuestAccessToken}) BEFORE login cleared it, and proves this device owned the session.
   * 409 if the account already has a live booking at that branch.
   */
  async claimGuestSession(guestToken: string): Promise<ApiResponse<{ claimed: boolean; bookingIds: string[]; branchIds: string[]; registeredUserId: string }>> {
    return this.request<any>('/customers/qr/claim', {
      method: 'POST',
      body: JSON.stringify({ guestToken })
    });
  }

  /** Get all bookings for the authenticated customer (optionally filtered by status). */
  async getMyBookings(status?: string): Promise<ApiResponse<{ bookings: any[]; count: number }>> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request<{ bookings: any[]; count: number }>(`/customers/bookings${qs}`, { method: 'GET' });
  }

  /** Cancel one of the customer's bookings. */
  async cancelBooking(bookingId: string, cancellationReason: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/bookings/${encodeURIComponent(bookingId)}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ cancellationReason })
    });
  }

  /** Attach or clear the customer's own pre-order (food/drink/song) on a booking. */
  async setBookingPreorder(bookingId: string, notes: string | null): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/bookings/${encodeURIComponent(bookingId)}/preorder`, {
      method: 'POST',
      body: JSON.stringify({ notes })
    });
  }

  /** Request to reschedule a booking to a new slot (organizer approves/declines). */
  async requestReschedule(bookingId: string, data: { bookingDate: string; startTime: string; endTime?: string; reason?: string }): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/bookings/${encodeURIComponent(bookingId)}/reschedule-request`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /** Start the order after check-in (CHECKED_IN -> ORDER_STARTED). */
  async startOrder(bookingId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/bookings/${encodeURIComponent(bookingId)}/start-order`, { method: 'POST' });
  }

  /** Join a branch waitlist (get notified when a table frees up). */
  async joinWaitlist(branchId: string, data: {
    requestedDate: string;
    tableId?: string;
    requestedStartTime?: string;
    requestedEndTime?: string;
    partySize?: number;
    notes?: string;
  }): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/waitlist`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /** List the customer's active waitlist entries. */
  async getMyWaitlist(): Promise<ApiResponse<{ entries: any[]; count: number }>> {
    return this.request<{ entries: any[]; count: number }>('/customers/waitlist', { method: 'GET' });
  }

  /** Leave (cancel) a waitlist entry. */
  async leaveWaitlist(waitlistId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/waitlist/${encodeURIComponent(waitlistId)}`, { method: 'DELETE' });
  }

  /** Submit a rating (1-5) and optional written feedback for a visited booking. */
  async submitFeedback(bookingId: string, customerRating: number, customerFeedback?: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/bookings/${encodeURIComponent(bookingId)}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ customerRating, customerFeedback })
    });
  }

  /** Get chair availability for a table in a given time slot. */
  async getChairsStatus(tableId: string, params: { branchId: string; bookingDate: string; startTime: string; endTime: string }): Promise<ApiResponse<{ chairs: Array<{ chairNumber: number; status: 'available' | 'booked' | 'unavailable' }> }>> {
    const qs = new URLSearchParams(params).toString();
    return this.request<any>(`/customers/tables/${encodeURIComponent(tableId)}/chairs-status?${qs}`, { method: 'GET' });
  }

  /** List my seat requests/assignments (pending, approved, rejected). */
  async getMyBookingSeats(): Promise<ApiResponse<{ seats: Array<{ id: string; bookingId: string; customerId: string; status?: string; chairNumber?: number; assignedByUserId?: string; booking?: any; [key: string]: any }> }>> {
    return this.request<any>('/customers/booking-seats/my-seats', { method: 'GET' });
  }

  /** List bookings that are open for join (arrived, table is_booking_opened, has capacity). */
  async getOpenForJoinBookings(branchId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/bookings/open-for-join`, { method: 'GET' });
  }

  /** Request to join a booking (creates pending seat request; organizer approves or rejects). */
  async requestSeat(bookingId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/bookings/${encodeURIComponent(bookingId)}/request-seat`, { method: 'POST' });
  }

  /** Create a grocery/shopping request. Sends only branchId and tableId (no date/time/guests). */
  async createGroceryBooking(branchId: string, tableId: string): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/bookings', {
      method: 'POST',
      body: JSON.stringify({ branchId, tableId })
    });
  }

  // ==================== Shop / Grocery – Shopping Requests (see docs/SHOP_GROCERY_API_AND_INTEGRATION.md) ====================

  /** Create a new shopping request (branchId + optional notes). No table/time. */
  async createShoppingRequest(data: { branchId: string; notes?: string }): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/shopping-requests', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /** List my shopping requests. Optional: ?status=requested */
  async getShoppingRequests(status?: string): Promise<ApiResponse<any[]>> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request<any[]>(`/customers/shopping-requests${query}`, { method: 'GET' });
  }

  /** Get one shopping request by ID */
  async getShoppingRequest(requestId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/shopping-requests/${encodeURIComponent(requestId)}`, { method: 'GET' });
  }

  /** Cancel my shopping request */
  async cancelShoppingRequest(requestId: string, body?: { cancellationReason?: string }): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/shopping-requests/${encodeURIComponent(requestId)}/cancel`, {
      method: 'POST',
      body: JSON.stringify(body || {})
    });
  }

  /** Get chat status for a shopping request */
  async getShoppingRequestChatStatus(requestId: string): Promise<ApiResponse<{ enabled: boolean; requestStatus?: string; message?: string }>> {
    return this.request<any>(`/customers/shopping-requests/${encodeURIComponent(requestId)}/chat/status`, { method: 'GET' });
  }

  /** List messages for a shopping request */
  async getShoppingRequestMessages(
    requestId: string,
    page: number = 1,
    limit: number = 50,
    beforeMessageId?: string
  ): Promise<ApiResponse<{ messages: any[]; total: number; page: number; limit: number; hasMore: boolean }>> {
    let url = `/customers/shopping-requests/${encodeURIComponent(requestId)}/messages?page=${page}&limit=${limit}`;
    if (beforeMessageId) url += `&beforeMessageId=${encodeURIComponent(beforeMessageId)}`;
    return this.request<any>(url, { method: 'GET' });
  }

  /** Send message (multipart if attachments, else JSON). At least one of messageText, savedListId, or attachments required. */
  async sendShoppingRequestMessage(
    requestId: string,
    payload: { messageText?: string; replyToMessageId?: string; savedListId?: string; attachments?: File[] }
  ): Promise<ApiResponse<any>> {
    const { messageText, replyToMessageId, savedListId, attachments } = payload;
    if ((!messageText || !messageText.trim()) && !savedListId && (!attachments || attachments.length === 0)) {
      return Promise.reject(new Error('At least one of messageText, savedListId, or attachments is required'));
    }
    if (attachments && attachments.length > 0) {
      const formData = new FormData();
      if (messageText?.trim()) formData.append('messageText', messageText.trim());
      if (replyToMessageId) formData.append('replyToMessageId', replyToMessageId);
      if (savedListId) formData.append('savedListId', savedListId);
      attachments.forEach((file) => formData.append('attachments', file));
      const baseURL = this.baseURL.replace(/\/+$/, '');
      const url = `${baseURL}/customers/shopping-requests/${encodeURIComponent(requestId)}/messages`;
      const { accessToken } = this.getTokens();
      const headers: Record<string, string> = {};
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      const response = await fetch(url, { method: 'POST', headers, body: formData });
      if (!response.ok) {
        const errText = await response.text();
        let msg = `HTTP ${response.status}`;
        try {
          const j = JSON.parse(errText);
          msg = j.message || msg;
        } catch { msg = errText || msg; }
        throw new Error(msg);
      }
      return response.json();
    }
    return this.request<any>(`/customers/shopping-requests/${encodeURIComponent(requestId)}/messages`, {
      method: 'POST',
      body: JSON.stringify({
        messageText: messageText?.trim() || '',
        replyToMessageId: replyToMessageId || undefined,
        savedListId: savedListId || undefined
      })
    });
  }

  /** Mark shopping request messages as read */
  async markShoppingRequestMessagesRead(requestId: string, messageIds?: string[]): Promise<ApiResponse<{ markedCount?: number }>> {
    const body = messageIds && messageIds.length > 0 ? { messageIds } : {};
    return this.request<any>(`/customers/shopping-requests/${encodeURIComponent(requestId)}/messages/read`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  /** Add like/dislike on a message */
  async addShoppingRequestMessageReaction(
    requestId: string,
    messageId: string,
    reactionType: 'like' | 'dislike'
  ): Promise<ApiResponse<any>> {
    return this.request<any>(
      `/customers/shopping-requests/${encodeURIComponent(requestId)}/messages/${encodeURIComponent(messageId)}/reaction`,
      { method: 'POST', body: JSON.stringify({ reactionType }) }
    );
  }

  /** Remove my reaction on a message */
  async removeShoppingRequestMessageReaction(requestId: string, messageId: string): Promise<ApiResponse<any>> {
    return this.request<any>(
      `/customers/shopping-requests/${encodeURIComponent(requestId)}/messages/${encodeURIComponent(messageId)}/reaction`,
      { method: 'DELETE' }
    );
  }

  /** Forward message to another of my shopping requests */
  async forwardShoppingRequestMessage(
    requestId: string,
    messageId: string,
    targetRequestId: string
  ): Promise<ApiResponse<any>> {
    return this.request<any>(
      `/customers/shopping-requests/${encodeURIComponent(requestId)}/messages/${encodeURIComponent(messageId)}/forward`,
      { method: 'POST', body: JSON.stringify({ targetRequestId }) }
    );
  }

  /** Get shareable URL for a shopping request */
  async getShoppingRequestShareLink(requestId: string): Promise<ApiResponse<{ shareUrl: string; requestId: string }>> {
    return this.request<any>(`/customers/shopping-requests/${encodeURIComponent(requestId)}/share`, { method: 'GET' });
  }

  // ==================== Chat Cart Orders (see docs/CHAT_CART_ORDERS_FRONTEND_IMPLEMENTATION.md) ====================

  /**
   * Create a cart order for a shopping request; returns cartOrder and shareUrl to send in chat.
   * Items may be catalog (`{ stockItemId, quantity }`) or manual free-text
   * (`{ itemName, quantityText?, note? }`), and the two may be mixed in one order.
   */
  async createCartOrder(
    requestId: string,
    body: {
      items: Array<{ stockItemId?: string; quantity?: number; itemName?: string; quantityText?: string; note?: string }>;
      notes?: string;
    }
  ): Promise<ApiResponse<{ cartOrder: any; shareUrl: string }>> {
    return this.request<any>(`/customers/shopping-requests/${encodeURIComponent(requestId)}/cart-orders`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** List my cart orders. Optional: requestId, branchId. */
  async getCartOrders(params?: { requestId?: string; branchId?: string }): Promise<ApiResponse<{ cartOrders: any[]; count: number }>> {
    const search = new URLSearchParams();
    if (params?.requestId) search.set('requestId', params.requestId);
    if (params?.branchId) search.set('branchId', params.branchId);
    const query = search.toString() ? `?${search.toString()}` : '';
    return this.request<any>(`/customers/cart-orders${query}`, { method: 'GET' });
  }

  /** Get a single cart order by ID (customer). */
  async getCartOrderDetail(cartOrderId: string): Promise<ApiResponse<{ cartOrder: any }>> {
    return this.request<any>(`/customers/cart-orders/${encodeURIComponent(cartOrderId)}`, { method: 'GET' });
  }

  /**
   * Edit an unfrozen cart order (replace items / notes). Resets the order to 'shared' so the
   * organizer re-prices it. Fails (409) if the order is already accepted/frozen.
   */
  async updateCartOrder(
    cartOrderId: string,
    body: {
      items: Array<{ stockItemId?: string; quantity?: number; itemName?: string; quantityText?: string; note?: string }>;
      notes?: string;
    }
  ): Promise<ApiResponse<{ cartOrder: any }>> {
    return this.request<any>(`/customers/cart-orders/${encodeURIComponent(cartOrderId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  /**
   * Respond to a priced cart order (organizer's quote): accept (freezes the order),
   * decline, or request changes (with an optional note).
   */
  async respondCartOrder(
    cartOrderId: string,
    body: { action: 'accept' | 'decline' | 'request_changes'; note?: string }
  ): Promise<ApiResponse<{ cartOrder: any }>> {
    return this.request<any>(`/customers/cart-orders/${encodeURIComponent(cartOrderId)}/respond`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ==================== Table service orders (pick & approve; from the check-in chat) ====================

  /** Place a table service order (catalogue + manual items) tied to the customer's check-in booking. */
  async createServiceOrder(body: {
    bookingId?: string;
    tableId?: string;
    branchId?: string;
    items: Array<{ stockItemId?: string | null; itemName: string; itemImage?: string | null; quantity?: number; isManual?: boolean; note?: string | null; unitPrice?: number | null; isComplimentary?: boolean }>;
    note?: string;
    orderType?: 'dine_in' | 'takeaway';
  }): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/service-orders`, { method: 'POST', body: JSON.stringify(body) });
  }

  /** Free-starter allowance for this bill (e.g. a whisky bottle grants 2 free starters). */
  async starterEntitlement(branchId: string, bookingId: string): Promise<{ granted: number; remaining: number; eligibleStarters: Array<{ id: string; name: string; image: string | null; price: number | null }> } | null> {
    try {
      const res = await this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/bookings/${encodeURIComponent(bookingId)}/starter-entitlement`, { method: 'GET' });
      return (res.data as any) || null;
    } catch { return null; }
  }

  /** The customer's recent/open service orders (with live status). */
  async getServiceOrders(): Promise<ApiResponse<any[]>> {
    return this.request<any>(`/customers/service-orders`, { method: 'GET' });
  }

  /**
   * Game/Sports venue live scoring — resolves the caller's active booking → its table → the live
   * game session on that table. `data` is the session dto, or null when no game is in play.
   */
  async getCurrentGameSession(): Promise<ApiResponse<any | null>> {
    return this.request<any>(`/customers/game/current`, { method: 'GET' });
  }

  /**
   * Apply a scoring action to the live game session on the caller's seated table. The server
   * authorizes — only a customer seated at that table may score (else 403). Returns the updated
   * session dto. Action shapes: {type:'points',player,delta}, {type:'frame',player},
   * {type:'set',player}, {type:'result',value}, {type:'undo'}.
   */
  async sendGameAction(action: any): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/game/action`, { method: 'POST', body: JSON.stringify({ action }) });
  }

  /** Opt in to be remembered as a "regular" at this venue → returns a device token to store. */
  async rememberAsRegular(bookingId: string, name?: string): Promise<ApiResponse<{ token: string; name: string | null; visitCount: number }>> {
    return this.request<any>(`/customers/regulars/remember`, { method: 'POST', body: JSON.stringify({ bookingId, name }) });
  }

  /** Cancel a still-pending order. */
  async cancelServiceOrder(orderId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/service-orders/${encodeURIComponent(orderId)}/cancel`, { method: 'POST' });
  }

  /** Edit a still-pending order (replace items and/or note). */
  async editServiceOrder(orderId: string, payload: { items?: Array<{ stockItemId?: string | null; itemName: string; itemImage?: string | null; quantity?: number; isManual?: boolean; note?: string | null; unitPrice?: number | null }>; note?: string }): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/service-orders/${encodeURIComponent(orderId)}`, { method: 'PATCH', body: JSON.stringify(payload) });
  }

  /** Full past-order / visit history (all statuses, newest first). */
  async getServiceOrderHistory(branchId?: string): Promise<ApiResponse<any[]>> {
    const qs = branchId ? `?branchId=${encodeURIComponent(branchId)}` : '';
    return this.request<any>(`/customers/service-orders/history${qs}`, { method: 'GET' });
  }

  /** The itemized tab (bill) for a booking: line items, subtotal/discount/tax/grand total, paid/due. */
  async getTableBill(bookingId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/bookings/${encodeURIComponent(bookingId)}/bill`, { method: 'GET' });
  }

  /** Start a tab payment for a booking (optional tip). Returns gateway order (or a mock order). */
  async createTabPayment(bookingId: string, tip?: number, payAmount?: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/tab-payments`, { method: 'POST', body: JSON.stringify({ bookingId, ...(tip != null ? { tip } : {}), ...(payAmount != null ? { payAmount } : {}) }) });
  }

  /** Verify a tab payment after the gateway (or mock) completes. */
  async verifyTabPayment(tabPaymentId: string, gatewayPaymentId: string, signature: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/tab-payments/verify`, { method: 'POST', body: JSON.stringify({ tabPaymentId, gatewayPaymentId, signature }) });
  }

  // ── Branch Groups (member side) ──────────────────────────────────────────
  async branchGroups(): Promise<any[]> {
    const res = await this.request<any[]>(`/customers/branch-groups`, { method: 'GET' });
    return (res.data as any[]) || [];
  }
  async acceptBranchGroup(groupId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/branch-groups/${encodeURIComponent(groupId)}/accept`, { method: 'POST' });
  }
  async leaveBranchGroup(groupId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/branch-groups/${encodeURIComponent(groupId)}/leave`, { method: 'POST' });
  }
  async branchGroupMessages(groupId: string): Promise<any[]> {
    const res = await this.request<any[]>(`/customers/branch-groups/${encodeURIComponent(groupId)}/messages`, { method: 'GET' });
    return (res.data as any[]) || [];
  }
  async postBranchGroupMessage(groupId: string, text: string): Promise<any> {
    const res = await this.request<any>(`/customers/branch-groups/${encodeURIComponent(groupId)}/messages`, { method: 'POST', body: JSON.stringify({ text }) });
    return res.data;
  }

  /** "Call staff": summon the table's assigned staff. Returns a ticket with live status. */
  async createServiceCall(body: { bookingId: string; callType?: string; note?: string; reasonLabel?: string }): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/service-calls`, { method: 'POST', body: JSON.stringify(body) });
  }

  /** The customer's recent/open staff calls (with live acknowledgement status). */
  async getServiceCalls(): Promise<ApiResponse<any[]>> {
    return this.request<any>(`/customers/service-calls`, { method: 'GET' });
  }

  /** Cancel an unanswered call ("never mind"). */
  async cancelServiceCall(ticketId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/service-calls/${encodeURIComponent(ticketId)}/cancel`, { method: 'POST' });
  }

  /** Post-service feedback (rate + optional tip). Response.reviewUrl is set for high ratings. */
  async submitServiceFeedback(body: { bookingId: string; ticketId?: string; rating: number; comment?: string; tipAmount?: number }): Promise<ApiResponse<{ id: string; rating: number; reviewUrl: string | null }>> {
    return this.request<{ id: string; rating: number; reviewUrl: string | null }>(`/customers/feedback`, { method: 'POST', body: JSON.stringify(body) });
  }

  /** Chat thread for a specific call (clarify the request with staff). */
  async getCallMessages(ticketId: string): Promise<ApiResponse<any[]>> {
    return this.request<any>(`/customers/service-calls/${encodeURIComponent(ticketId)}/messages`, { method: 'GET' });
  }

  async sendCallMessage(ticketId: string, message: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/service-calls/${encodeURIComponent(ticketId)}/messages`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }

  /**
   * Quick song request (gated by the global `quick_songs` platform flag): paste a YouTube link
   * from inside "Call staff". Returns a ServiceTicket that flows through the same
   * `service_ticket:updated` realtime, carrying `songState` ('queued'|'playing'|'played').
   * Throws with the server message on 400 (invalid URL) / 429 (rate-limited).
   */
  async createSongRequest(bookingId: string, url: string): Promise<ApiResponse<{ id: string; mediaTitle?: string; mediaThumbnail?: string; songState?: string; votes?: number }>> {
    return this.request<any>(`/customers/service-calls/song`, {
      method: 'POST',
      body: JSON.stringify({ bookingId, url }),
    });
  }

  /** Live song queue for a branch (now-playing first, then by votes) — for the guest upvote view. */
  async getSongQueue(branchId: string): Promise<ApiResponse<Array<{ id: string; mediaTitle?: string | null; mediaThumbnail?: string | null; votes: number; songState?: string | null }>>> {
    return this.request<any[]>(`/customers/branches/${encodeURIComponent(branchId)}/song-queue`, { method: 'GET' });
  }

  /** Upvote a queued song. */
  async upvoteSong(ticketId: string): Promise<ApiResponse<{ id: string; votes: number }>> {
    return this.request<{ id: string; votes: number }>(`/customers/song-requests/${encodeURIComponent(ticketId)}/upvote`, { method: 'POST' });
  }

  // ==================== Live Song Request (see docs/LIVE_SONG_REQUEST_CUSTOMER_IMPLEMENTATION.md) ====================

  /** Check if Live Song Request is available for branch, arrived bookings, and whether comments are enabled. */
  async getLiveSongAvailable(branchId: string): Promise<ApiResponse<{ available: boolean; arrivedBookings?: Array<{ id: string; tableName?: string }>; commentsEnabled?: boolean }>> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/live-song-available`, { method: 'GET' });
  }

  /** Submit a live song request. Uses multipart/form-data when requestGroupPhoto or sourceFile is provided. Source: use either sourceLink or sourceFile, not both. */
  async createLiveSongRequest(
    branchId: string,
    body: {
      bookingId: string;
      songDescription: string;
      sourceLink?: string;
      customerComments?: string;
      fileUrl?: string;
      photoUrl?: string;
    },
    requestGroupPhoto?: File,
    sourceFile?: File
  ): Promise<ApiResponse<{ request: any }>> {
    const useFormData = !!(requestGroupPhoto || sourceFile);

    if (useFormData) {
      const formData = new FormData();
      formData.append('bookingId', body.bookingId);
      formData.append('songDescription', body.songDescription);
      if (body.customerComments) formData.append('customerComments', body.customerComments);
      if (body.fileUrl) formData.append('fileUrl', body.fileUrl);
      if (body.photoUrl) formData.append('photoUrl', body.photoUrl);
      if (sourceFile) {
        formData.append('sourceFile', sourceFile, sourceFile.name);
      } else if (body.sourceLink) {
        formData.append('sourceLink', body.sourceLink);
      }
      if (requestGroupPhoto) {
        formData.append('requestGroupPhoto', requestGroupPhoto, requestGroupPhoto.name);
      }

      const baseURL = this.baseURL.replace(/\/+$/, '');
      const url = `${baseURL}/customers/branches/${encodeURIComponent(branchId)}/live-song-requests`;
      const { accessToken } = this.getTokens();
      const headers: Record<string, string> = {};
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      const data = await response.json();
      return data;
    }

    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/live-song-requests`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** List my live song requests for branch (paginated). */
  async getLiveSongRequests(
    branchId: string,
    params?: { page?: number; limit?: number }
  ): Promise<ApiResponse<{ requests: any[]; total?: number; page?: number; limit?: number }>> {
    const search = new URLSearchParams();
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.limit != null) search.set('limit', String(params.limit));
    const query = search.toString() ? `?${search.toString()}` : '';
    return this.request<any>(
      `/customers/branches/${encodeURIComponent(branchId)}/live-song-requests${query}`,
      { method: 'GET' }
    );
  }

  /** Get all customers' song requests for branch with pagination. Includes scheduledSongs queue, myRequestPosition, myRequestStatus. */
  async getLiveSongAllRequests(
    branchId: string,
    params?: { page?: number; limit?: number }
  ): Promise<
    ApiResponse<{
      requests: any[];
      scheduledSongs?: Array<{ id: string; songDescription?: string; status?: string; sourceLink?: string; [key: string]: any }>;
      myRequestPosition?: number;
      myRequestStatus?: string;
      total?: number;
      page?: number;
      limit?: number;
    }>
  > {
    const search = new URLSearchParams();
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.limit != null) search.set('limit', String(Math.min(params.limit ?? 20, 100)));
    const query = search.toString() ? `?${search.toString()}` : '';
    return this.request<any>(
      `/customers/branches/${encodeURIComponent(branchId)}/live-song-requests/all${query}`,
      { method: 'GET' }
    );
  }

  /** Get current (now playing) live song request for branch. Comments allowed only on this request. */
  async getLiveSongCurrentRequest(branchId: string): Promise<ApiResponse<{ request: any } | null>> {
    return this.request<any>(`/customers/branches/${encodeURIComponent(branchId)}/live-song-requests/current`, { method: 'GET' });
  }

  /** Get one live song request by ID. */
  async getLiveSongRequest(requestId: string): Promise<ApiResponse<{ request: any }>> {
    return this.request<any>(`/customers/live-song-requests/${encodeURIComponent(requestId)}`, { method: 'GET' });
  }

  /** Get comments for a live song request. */
  async getLiveSongRequestComments(requestId: string): Promise<ApiResponse<{ comments: any[] }>> {
    return this.request<any>(`/customers/live-song-requests/${encodeURIComponent(requestId)}/comments`, { method: 'GET' });
  }

  /** Add a comment to a live song request. Supports text and/or images/videos (multipart). Only the customer who requested the song can upload. Max 10 files, 50MB each (images and videos). */
  async addLiveSongRequestComment(
    requestId: string,
    payload: { commentText?: string; commentImages?: File[] }
  ): Promise<ApiResponse<{ comment: any }>> {
    const { commentText, commentImages } = payload;
    const hasImages = commentImages && commentImages.length > 0;
    if (hasImages) {
      const formData = new FormData();
      if (commentText?.trim()) formData.append('commentText', commentText.trim());
      commentImages!.forEach((file) => formData.append('commentImages', file, file.name));
      const baseURL = this.baseURL.replace(/\/+$/, '');
      const url = `${baseURL}/customers/live-song-requests/${encodeURIComponent(requestId)}/comments`;
      const { accessToken } = this.getTokens();
      const headers: Record<string, string> = {};
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!response.ok) {
        const errorText = await response.text();
        let message = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const err = JSON.parse(errorText);
          message = err.message || message;
        } catch {
          if (errorText) message = errorText;
        }
        throw new Error(message);
      }
      const data = await response.json();
      return data;
    }
    return this.request<any>(`/customers/live-song-requests/${encodeURIComponent(requestId)}/comments`, {
      method: 'POST',
      body: JSON.stringify({ commentText: commentText || '' }),
    });
  }

  // ==================== Live Song Request – Organizer (see docs/LIVE_SONG_ORGANIZER_IMPLEMENTATION.md) ====================
  // Use these in the organizer portal. Organizer can create their own request, approve it, and add comments with files (promotions, etc.).

  /** Create a live song request as organizer (no customer booking). For promotions/announcements on screen. */
  async createLiveSongRequestAsOrganizer(
    branchId: string,
    body: {
      songDescription: string;
      sourceLink?: string;
      customerComments?: string;
      fileUrl?: string;
      photoUrl?: string;
    }
  ): Promise<ApiResponse<{ request: any }>> {
    return this.request<any>(`/organizers/branches/${encodeURIComponent(branchId)}/live-song-requests`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** Organizer: approve a live song request (set to scheduled, set final source). */
  async approveLiveSongRequestAsOrganizer(
    requestId: string,
    body?: { finalSourceType?: string; finalSourceUrl?: string }
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/organizers/live-song-requests/${encodeURIComponent(requestId)}/approve`, {
      method: 'PATCH',
      body: JSON.stringify(body || {}),
    });
  }

  /** Organizer: set request as current (now playing). */
  async setLiveSongRequestCurrentAsOrganizer(requestId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/organizers/live-song-requests/${encodeURIComponent(requestId)}/set-current`, {
      method: 'PATCH',
    });
  }

  /** Organizer: set request as closed (song over). */
  async setLiveSongRequestClosedAsOrganizer(requestId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/organizers/live-song-requests/${encodeURIComponent(requestId)}/set-closed`, {
      method: 'PATCH',
    });
  }

  /** Organizer: add comment on a live song request (e.g. promotions, images/videos/audio). Multipart when commentImages provided. */
  async addLiveSongRequestCommentAsOrganizer(
    requestId: string,
    payload: { commentText?: string; commentImages?: File[] }
  ): Promise<ApiResponse<{ comment: any }>> {
    const { commentText, commentImages } = payload;
    const hasImages = commentImages && commentImages.length > 0;
    if (hasImages) {
      const formData = new FormData();
      if (commentText?.trim()) formData.append('commentText', commentText.trim());
      commentImages!.forEach((file) => formData.append('commentImages', file, file.name));
      const baseURL = this.baseURL.replace(/\/+$/, '');
      const url = `${baseURL}/organizers/live-song-requests/${encodeURIComponent(requestId)}/comments`;
      const { accessToken } = this.getTokens();
      const headers: Record<string, string> = {};
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!response.ok) {
        const errorText = await response.text();
        let message = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const err = JSON.parse(errorText);
          message = err.message || message;
        } catch {
          if (errorText) message = errorText;
        }
        throw new Error(message);
      }
      const data = await response.json();
      return data;
    }
    return this.request<any>(`/organizers/live-song-requests/${encodeURIComponent(requestId)}/comments`, {
      method: 'POST',
      body: JSON.stringify({ commentText: commentText || '' }),
    });
  }

  // Saved shopping lists (customer-only)
  async getSavedShoppingLists(): Promise<ApiResponse<any[]>> {
    return this.request<any[]>('/customers/saved-shopping-lists', { method: 'GET' });
  }

  async createSavedShoppingList(data: {
    name: string;
    branchId?: string;
    items?: Array<{ itemText: string; quantity?: string; sortOrder?: number }>;
  }): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/saved-shopping-lists', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getSavedShoppingList(listId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/saved-shopping-lists/${encodeURIComponent(listId)}`, { method: 'GET' });
  }

  async updateSavedShoppingList(
    listId: string,
    data: { name?: string; branchId?: string; items?: Array<{ itemText: string; quantity?: string; sortOrder?: number }> }
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/saved-shopping-lists/${encodeURIComponent(listId)}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteSavedShoppingList(listId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/saved-shopping-lists/${encodeURIComponent(listId)}`, { method: 'DELETE' });
  }

  // Get customer booking activities
  async getCustomerActivities(): Promise<ApiResponse<{
    activities: Array<{
      branchId: string;
      branchName: string;
      branchLogo?: string;
      checkoutComments?: string;
      paymentAmount?: number;
      status: string;
      date?: string;
      startTime?: string;
      endTime?: string;
    }>;
    count: number;
  }>> {
    return this.request<{
      activities: Array<{
        branchId: string;
        branchName: string;
        branchLogo?: string;
        checkoutComments?: string;
        paymentAmount?: number;
        status: string;
        date?: string;
        startTime?: string;
        endTime?: string;
      }>;
      count: number;
    }>('/customers/bookings/activities', {
      method: 'GET'
    });
  }

  // Get tournament details with complete status (for live viewing)
  async getTournamentDetails(tournamentId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/tournaments/${encodeURIComponent(tournamentId)}`, {
      method: 'GET'
    });
  }

  // Customer tournament registration
  async registerForTournament(
    tournamentId: string,
    paymentData?: {
      paymentAmount?: number;
      paymentMethod?: 'upi' | 'card' | 'cash';
      paymentStatus?: 'paid' | 'pending' | 'failed';
    }
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/tournaments/${encodeURIComponent(tournamentId)}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(paymentData || {}),
    });
  }

  // Customer tournament unregistration
  async unregisterFromTournament(tournamentId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/tournaments/${encodeURIComponent(tournamentId)}/register`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  // Note: Removed checkRegistrationStatus method to prevent automatic registrations
  // Registration status should only be checked when user explicitly clicks register button
  // The handleJoinMatch method will handle detecting "already registered" responses

  // Customer Profile endpoints
  async getCustomerProfile(): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/profile', {
      method: 'GET'
    });
  }

  async verifyCustomerProfile(): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/profile/verify', {
      method: 'GET'
    });
  }

  /** Get Firebase VAPID key for FCM. No auth required (public config). */
  async getFirebaseVapidKey(): Promise<ApiResponse<{ key: string; value: string }>> {
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const url = `${baseURL}/config/firebase/vapid-key`;
    const res = await fetch(url, { method: 'GET' });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);
    return data;
  }

  /** Register FCM push token (Customer Portal). Call after login. */
  async registerPushToken(payload: {
    fcmToken: string;
    deviceId?: string;
    platform?: 'web' | 'android' | 'ios';
  }): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/push-token', {
      method: 'POST',
      body: JSON.stringify({
        fcmToken: payload.fcmToken,
        ...(payload.deviceId != null && { deviceId: payload.deviceId }),
        platform: payload.platform ?? 'web',
      }),
    });
  }

  /** Unregister FCM push token (Customer Portal). Call on logout. */
  async unregisterPushToken(payload?: { fcmToken?: string; deviceId?: string }): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/push-token', {
      method: 'DELETE',
      body: JSON.stringify(payload ?? {}),
    });
  }

  // Get public players
  async getPublicPlayers(params?: {
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<any[]>> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    
    const queryString = queryParams.toString();
    const endpoint = `/customers/players${queryString ? `?${queryString}` : ''}`;
    
    return this.request<any[]>(endpoint, {
      method: 'GET'
    });
  }

  async updateCustomerProfile(profileData: {
    firstName: string; // Mandatory
    lastName: string; // Mandatory
    displayName: string; // Mandatory
    tagline?: string;
    phone?: string;
    profilePicture?: File; // File to upload (use 'profilePicture' for FormData)
    profilePic?: string; // URL (alternative to file upload, use 'profilePic' for JSON)
    profileMode?: 'public' | 'private' | 'privacy';
    city?: string;
    state?: string;
    country?: string;
    skillLevel?: 'beginner' | 'intermediate' | 'advanced' | 'expert';
    rating?: number;
    preferredGameTypes?: string[]; // Will be sent as array in FormData
  }): Promise<ApiResponse<any>> {
    const formData = new FormData();
    
    // Mandatory fields
    formData.append('firstName', profileData.firstName);
    formData.append('lastName', profileData.lastName);
    formData.append('displayName', profileData.displayName);
    
    // Optional fields
    if (profileData.tagline) {
      formData.append('tagline', profileData.tagline);
    }
    if (profileData.phone) {
      formData.append('phone', profileData.phone);
    }
    if (profileData.profileMode) {
      formData.append('profileMode', profileData.profileMode);
    }
    if (profileData.city) {
      formData.append('city', profileData.city);
    }
    if (profileData.state) {
      formData.append('state', profileData.state);
    }
    if (profileData.country) {
      formData.append('country', profileData.country);
    }
    if (profileData.skillLevel) {
      formData.append('skillLevel', profileData.skillLevel);
    }
    if (profileData.rating !== undefined) {
      formData.append('rating', profileData.rating.toString());
    }
    if (profileData.preferredGameTypes && profileData.preferredGameTypes.length > 0) {
      // For FormData, append each item separately
      profileData.preferredGameTypes.forEach((gameType, index) => {
        formData.append(`preferredGameTypes[${index}]`, gameType);
      });
    }
    
    // Add profile picture (file takes precedence over URL)
    if (profileData.profilePicture) {
      formData.append('profilePicture', profileData.profilePicture);
    } else if (profileData.profilePic) {
      formData.append('profilePic', profileData.profilePic);
    }

    const url = `${this.baseURL}/customers/profile`;
    const { accessToken } = this.getTokens();
    const headers: Record<string, string> = {
      // Don't set Content-Type - browser will set it with boundary for FormData
    };
    
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    try {
      console.log('Updating customer profile:', url);
      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: formData,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP error! status: ${response.status}`;
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        
        if (response.status === 401) {
          try {
            this.clearTokens();
          } catch {}
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Profile updated:', data);
      
      return data;
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    }
  }

  // Note: uploadProfilePicture method removed - profile picture upload is now handled directly in updateCustomerProfile

  // Friends Management endpoints
  async getFriends(): Promise<ApiResponse<{ friends: any[]; count?: number }>> {
    return this.request<{ friends: any[]; count?: number }>('/customers/friends', {
      method: 'GET'
    });
  }

  async getFriendRequests(): Promise<ApiResponse<{ received: any[]; sent: any[]; counts?: { received: number; sent: number } }>> {
    return this.request<{ received: any[]; sent: any[]; counts?: { received: number; sent: number } }>('/customers/friend-requests', {
      method: 'GET'
    });
  }

  async sendFriendRequest(recipientId: string, message?: string): Promise<ApiResponse<any>> {
    const body: { recipientId: string; message?: string } = { recipientId };
    if (message && message.trim()) {
      body.message = message.trim();
    }
    return this.request<any>('/customers/friend-requests', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async acceptFriendRequest(requestId: string, comment?: string): Promise<ApiResponse<any>> {
    const body: { requestId: string; comment?: string } = { requestId };
    if (comment && comment.trim()) {
      body.comment = comment.trim();
    }
    return this.request<any>('/customers/friend-requests/accept', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async rejectFriendRequest(requestId: string, comment?: string): Promise<ApiResponse<any>> {
    const body: { requestId: string; comment?: string } = { requestId };
    if (comment && comment.trim()) {
      body.comment = comment.trim();
    }
    return this.request<any>('/customers/friend-requests/reject', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async cancelFriendRequest(requestId: string): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/friend-requests/cancel', {
      method: 'POST',
      body: JSON.stringify({ requestId }),
    });
  }

  // Chat/Messaging endpoints
  async getConversation(friendId: string, page: number = 1, limit: number = 50, before?: string, markAsRead: boolean = false): Promise<ApiResponse<{ messages: any[]; pagination: any }>> {
    let url = `/customers/messages/conversation?friendId=${encodeURIComponent(friendId)}&page=${page}&limit=${limit}`;
    if (before) {
      url += `&before=${encodeURIComponent(before)}`;
    }
    if (markAsRead) {
      url += `&markAsRead=true`;
    }
    return this.request<{ messages: any[]; pagination: any }>(url, {
      method: 'GET'
    });
  }

  async sendMessage(
    recipientId: string, 
    messageText: string, 
    replyToMessageId?: string, 
    attachments?: File[],
    location?: {
      latitude: number;
      longitude: number;
      address?: string;
      placeName?: string;
    }
  ): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('recipientId', recipientId);
    
    // messageText is optional if attachments or location are provided
    if (messageText) {
      formData.append('messageText', messageText);
    }
    
    if (replyToMessageId) {
      formData.append('replyToMessageId', replyToMessageId);
    }
    
    if (attachments && attachments.length > 0) {
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }

    // Add location data if provided
    if (location) {
      formData.append('location[latitude]', location.latitude.toString());
      formData.append('location[longitude]', location.longitude.toString());
      if (location.address) {
        formData.append('location[address]', location.address);
      }
      if (location.placeName) {
        formData.append('location[placeName]', location.placeName);
      }
    }

    const { accessToken } = this.getTokens();
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    // Don't set Content-Type for FormData, browser will set it with boundary

    const response = await fetch(`${this.baseURL}/customers/messages`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  }

  async markMessagesAsRead(friendId: string, messageIds?: string[]): Promise<ApiResponse<{ markedCount: number }>> {
    const body: any = { friendId };
    if (messageIds && messageIds.length > 0) {
      body.messageIds = messageIds;
    }
    return this.request<{ markedCount: number }>('/customers/messages/mark-read', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  async getUnreadCount(): Promise<ApiResponse<{ unreadCount: number; conversations: any[] }>> {
    return this.request<{ unreadCount: number; conversations: any[] }>('/customers/messages/unread-count', {
      method: 'GET'
    });
  }

  /**
   * Forward a message from DM or group to another DM or group.
   * sourceType: 'dm' | 'group', sourceMessageId: UUID
   * destinationType: 'dm' | 'group', then destinationFriendId (profile ID) or destinationGroupId
   */
  async forwardMessage(params: {
    sourceType: 'dm' | 'group';
    sourceMessageId: string;
    destinationType: 'dm' | 'group';
    destinationFriendId?: string;
    destinationGroupId?: string;
  }): Promise<ApiResponse<{ type: 'dm' | 'group'; message: any }>> {
    const { sourceType, sourceMessageId, destinationType, destinationFriendId, destinationGroupId } = params;
    const body: any = { sourceType, sourceMessageId, destinationType };
    if (destinationType === 'dm') {
      if (!destinationFriendId) throw new Error('destinationFriendId is required when destinationType is dm');
      body.destinationFriendId = destinationFriendId;
    } else {
      if (!destinationGroupId) throw new Error('destinationGroupId is required when destinationType is group');
      body.destinationGroupId = destinationGroupId;
    }
    return this.request<{ type: 'dm' | 'group'; message: any }>('/customers/messages/forward', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async removeFriend(friendId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/friends/${encodeURIComponent(friendId)}`, {
      method: 'DELETE',
    });
  }

  // Customer Groups endpoints - Base path: /customers/groups
  async createGroup(
    name: string,
    description?: string,
    profilePic?: File,
    memberIds?: string[]
  ): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('name', name);
    
    if (description) {
      formData.append('description', description);
    }
    
    if (profilePic) {
      formData.append('profilePic', profilePic);
    }
    
    if (memberIds && memberIds.length > 0) {
      formData.append('memberIds', JSON.stringify(memberIds));
    }

    const { accessToken } = this.getTokens();
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${this.baseURL}/customers/groups`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  }

  async getGroups(page: number = 1, limit: number = 20): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/customers/groups?page=${page}&limit=${limit}`, {
      method: 'GET'
    });
  }

  async getGroupDetails(groupId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/groups/${encodeURIComponent(groupId)}`, {
      method: 'GET'
    });
  }

  async updateGroup(
    groupId: string,
    name?: string,
    description?: string,
    profilePic?: File
  ): Promise<ApiResponse<any>> {
    const formData = new FormData();
    
    if (name) {
      formData.append('name', name);
    }
    
    if (description !== undefined) {
      formData.append('description', description);
    }
    
    if (profilePic) {
      formData.append('profilePic', profilePic);
    }

    const { accessToken } = this.getTokens();
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${this.baseURL}/customers/groups/${encodeURIComponent(groupId)}`, {
      method: 'PUT',
      headers,
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  }

  async deleteGroup(groupId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/groups/${encodeURIComponent(groupId)}`, {
      method: 'DELETE',
    });
  }

  // Group Invitations
  async inviteToGroup(groupId: string, friendIds: string[], invitationMessage?: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/groups/${encodeURIComponent(groupId)}/invite`, {
      method: 'POST',
      body: JSON.stringify({ 
        friendIds,
        invitationMessage 
      }),
    });
  }

  async getPendingInvitations(): Promise<ApiResponse<any[]>> {
    return this.request<any[]>('/customers/groups/invitations/pending', {
      method: 'GET'
    });
  }

  async respondToInvitation(invitationId: string, action: 'accept' | 'reject'): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/groups/invitations/${encodeURIComponent(invitationId)}/respond`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  }

  // Group Members Management
  async addGroupMembers(groupId: string, memberIds: string[]): Promise<ApiResponse<{ added: number; group: any }>> {
    return this.request<{ added: number; group: any }>(`/customers/groups/${encodeURIComponent(groupId)}/members`, {
      method: 'POST',
      body: JSON.stringify({ memberIds }),
    });
  }

  async removeGroupMember(groupId: string, memberId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}`, {
      method: 'DELETE',
    });
  }

  // Group Posts
  async getGroupPosts(groupId: string, page: number = 1, limit: number = 20): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/groups/${encodeURIComponent(groupId)}/posts?page=${page}&limit=${limit}`, {
      method: 'GET'
    });
  }

  async getPostDetails(postId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/groups/posts/${encodeURIComponent(postId)}`, {
      method: 'GET'
    });
  }

  async createGroupPost(groupId: string, postData: {
    contentText: string;
    attachments?: Array<{
      attachmentType: 'image' | 'video' | 'youtube' | 'facebook' | 'twitter' | 'instagram' | 'link';
      fileUrl?: string;
      linkUrl?: string;
      thumbnailUrl?: string;
    }>;
    parentPostId?: string;
  }): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/groups/${encodeURIComponent(groupId)}/posts`, {
      method: 'POST',
      body: JSON.stringify(postData),
    });
  }

  async deleteGroupPost(groupId: string, postId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/groups/${encodeURIComponent(groupId)}/posts/${encodeURIComponent(postId)}`, {
      method: 'DELETE',
    });
  }

  // Group Post Comments
  async getPostComments(postId: string, page: number = 1, limit: number = 50): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/groups/posts/${encodeURIComponent(postId)}/comments?page=${page}&limit=${limit}`, {
      method: 'GET'
    });
  }

  async createPostComment(postId: string, commentData: {
    commentText: string;
    parentCommentId?: string;
  }): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/groups/posts/${encodeURIComponent(postId)}/comments`, {
      method: 'POST',
      body: JSON.stringify(commentData),
    });
  }

  async deletePostComment(postId: string, commentId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/groups/posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}`, {
      method: 'DELETE',
    });
  }

  // Group Chat Messages
  async getGroupMessages(groupId: string, page: number = 1, limit: number = 50, before?: string): Promise<ApiResponse<{ messages: any[]; pagination: any }>> {
    let url = `/customers/groups/${encodeURIComponent(groupId)}/messages?page=${page}&limit=${limit}`;
    if (before) {
      url += `&before=${encodeURIComponent(before)}`;
    }
    return this.request<{ messages: any[]; pagination: any }>(url, {
      method: 'GET'
    });
  }

  async sendGroupMessage(
    groupId: string, 
    messageText: string, 
    replyToMessageId?: string, 
    attachments?: File[],
    location?: {
      latitude: number;
      longitude: number;
      address?: string;
      placeName?: string;
    }
  ): Promise<ApiResponse<any>> {
    const formData = new FormData();
    
    // messageText is optional if attachments or location are provided
    if (messageText) {
      formData.append('messageText', messageText);
    }
    
    if (replyToMessageId) {
      formData.append('replyToMessageId', replyToMessageId);
    }
    
    if (attachments && attachments.length > 0) {
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }

    // Add location data if provided
    if (location) {
      formData.append('location[latitude]', location.latitude.toString());
      formData.append('location[longitude]', location.longitude.toString());
      if (location.address) {
        formData.append('location[address]', location.address);
      }
      if (location.placeName) {
        formData.append('location[placeName]', location.placeName);
      }
    }

    const { accessToken } = this.getTokens();
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    // Don't set Content-Type for FormData, browser will set it with boundary

    const response = await fetch(`${this.baseURL}/customers/groups/${encodeURIComponent(groupId)}/messages`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  }

  // Group Message Comments
  async createGroupMessageComment(
    messageId: string,
    commentText: string,
    replyToCommentId?: string,
    attachments?: File[],
    location?: {
      latitude: number;
      longitude: number;
      address?: string;
      placeName?: string;
    }
  ): Promise<ApiResponse<any>> {
    const formData = new FormData();
    
    // commentText is optional if attachments or location are provided
    if (commentText) {
      formData.append('commentText', commentText);
    }
    
    if (replyToCommentId) {
      formData.append('replyToCommentId', replyToCommentId);
    }
    
    if (attachments && attachments.length > 0) {
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }

    // Add location data if provided
    if (location) {
      formData.append('location[latitude]', location.latitude.toString());
      formData.append('location[longitude]', location.longitude.toString());
      if (location.address) {
        formData.append('location[address]', location.address);
      }
      if (location.placeName) {
        formData.append('location[placeName]', location.placeName);
      }
    }

    const { accessToken } = this.getTokens();
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${this.baseURL}/customers/groups/messages/${encodeURIComponent(messageId)}/comments`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  }

  async getGroupMessageComments(messageId: string, page: number = 1, limit: number = 50): Promise<ApiResponse<{ comments: any[]; pagination: any }>> {
    return this.request<{ comments: any[]; pagination: any }>(`/customers/groups/messages/${encodeURIComponent(messageId)}/comments?page=${page}&limit=${limit}`, {
      method: 'GET'
    });
  }

  // Comment Sentiment
  async addCommentSentiment(commentId: string, sentiment: 'positive' | 'negative'): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/groups/comments/${encodeURIComponent(commentId)}/sentiment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ sentiment })
    });
  }

  async removeCommentSentiment(commentId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/groups/comments/${encodeURIComponent(commentId)}/sentiment`, {
      method: 'DELETE'
    });
  }

  // Booking Chat endpoints - Using booking ID (NOT table ID)
  async getBookingChatMessages(bookingId: string, page: number = 1, limit: number = 50, beforeMessageId?: string): Promise<ApiResponse<{ messages: any[]; total: number; page: number; limit: number; hasMore: boolean }>> {
    let url = `/customers/bookings/${encodeURIComponent(bookingId)}/messages?page=${page}&limit=${limit}`;
    if (beforeMessageId) {
      url += `&beforeMessageId=${encodeURIComponent(beforeMessageId)}`;
    }
    return this.request<{ messages: any[]; total: number; page: number; limit: number; hasMore: boolean }>(url, {
      method: 'GET'
    });
  }

  async sendBookingChatMessage(
    bookingId: string,
    messageText: string,
    replyToMessageId?: string,
    attachments?: File[],
    sourceLang?: string
  ): Promise<ApiResponse<any>> {
    // If attachments are provided, use multipart/form-data
    if (attachments && attachments.length > 0) {
      const formData = new FormData();
      formData.append('messageText', messageText);
      if (replyToMessageId) {
        formData.append('replyToMessageId', replyToMessageId);
      }
      if (sourceLang) formData.append('sourceLang', sourceLang);
      
      // Append all files
      attachments.forEach(file => {
        formData.append('attachments', file);
      });

      // Use custom request for FormData (don't set Content-Type header - browser will set it with boundary)
      const baseURL = this.baseURL.replace(/\/+$/, '');
      const normalizedEndpoint = `/customers/bookings/${encodeURIComponent(bookingId)}/messages`;
      const url = `${baseURL}${normalizedEndpoint}`;
      
      const { accessToken } = this.getTokens();
      const headers: Record<string, string> = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } else {
      // No attachments - use JSON
      return this.request<any>(`/customers/bookings/${encodeURIComponent(bookingId)}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messageText,
          replyToMessageId,
          ...(sourceLang ? { sourceLang } : {})
        })
      });
    }
  }

  async markBookingChatMessagesAsRead(bookingId: string, messageIds?: string[]): Promise<ApiResponse<{ markedCount: number }>> {
    const body: { messageIds?: string[] } = {};
    if (messageIds && messageIds.length > 0) {
      body.messageIds = messageIds;
    }
    return this.request<{ markedCount: number }>(`/customers/bookings/${encodeURIComponent(bookingId)}/messages/read`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
  }

  // Get chat status - check if chat is enabled for this booking
  async getBookingChatStatus(bookingId: string): Promise<ApiResponse<{ enabled: boolean; bookingStatus: string; message?: string; branchId?: string }>> {
    return this.request<{ enabled: boolean; bookingStatus: string; message?: string }>(`/customers/bookings/${encodeURIComponent(bookingId)}/chat/status`, {
      method: 'GET'
    });
  }

  /** Send typing indicator for booking chat (REST; server publishes to Ably). 204 No Content. */
  async sendBookingChatTyping(bookingId: string, isTyping: boolean): Promise<void> {
    await this.request(`/customers/bookings/${encodeURIComponent(bookingId)}/typing`, {
      method: 'POST',
      body: JSON.stringify({ isTyping }),
    });
  }

  // ---- Table GROUP chat (organizer + all checked-in customers at a table) ----

  /** Fetch the table group chat history. Only available to checked-in members while the table is occupied. */
  async getTableGroupChatMessages(bookingId: string, page: number = 1, limit: number = 100, beforeMessageId?: string): Promise<ApiResponse<{ messages: any[]; pagination: { page: number; limit: number; total: number; hasMore: boolean }; enabled: boolean; archived: boolean; bookingStatus: string }>> {
    let url = `/customers/bookings/${encodeURIComponent(bookingId)}/group-messages?page=${page}&limit=${limit}`;
    if (beforeMessageId) url += `&beforeMessageId=${encodeURIComponent(beforeMessageId)}`;
    return this.request(url, { method: 'GET' });
  }

  /** Send a message to the table group chat. Rejected once the customer has checked out. */
  async sendTableGroupChatMessage(bookingId: string, messageText: string): Promise<ApiResponse<any>> {
    return this.request(`/customers/bookings/${encodeURIComponent(bookingId)}/group-messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messageText }),
    });
  }

  /** Whether the table group chat is available to this customer (member + occupied). */
  async getTableGroupChatStatus(bookingId: string): Promise<ApiResponse<{ enabled: boolean; archived: boolean; bookingStatus: string; members: any[] }>> {
    return this.request(`/customers/bookings/${encodeURIComponent(bookingId)}/group-chat/status`, { method: 'GET' });
  }

  /** Typing indicator for the table group chat (REST; server publishes to Ably). 204 No Content. */
  async sendTableGroupChatTyping(bookingId: string, isTyping: boolean): Promise<void> {
    await this.request(`/customers/bookings/${encodeURIComponent(bookingId)}/group-typing`, {
      method: 'POST',
      body: JSON.stringify({ isTyping }),
    });
  }

  /**
   * Send typing indicator for shopping-request chat (REST; server publishes
   * `shopping_request:typing` to Ably, directed to the organizer's branch channel).
   * Returns `{ success: true }`.
   */
  async sendShoppingRequestTyping(requestId: string, isTyping: boolean): Promise<void> {
    await this.request(`/customers/shopping-requests/${encodeURIComponent(requestId)}/typing`, {
      method: 'POST',
      body: JSON.stringify({ isTyping }),
    });
  }

  // ==================== Tournament Chat API (Customer Portal) ====================
  // See docs/TOURNAMENT_CHAT_CUSTOMER_PORTAL_IMPLEMENTATION.md

  async getTournamentChatConversations(
    tournamentId: string,
    params?: { type?: string; page?: number; limit?: number }
  ): Promise<ApiResponse<{ conversations: any[]; total: number; page: number; limit: number }>> {
    let url = `/customers/tournaments/${encodeURIComponent(tournamentId)}/chat/conversations?page=${params?.page ?? 1}&limit=${params?.limit ?? 50}`;
    if (params?.type) url += `&type=${encodeURIComponent(params.type)}`;
    return this.request<{ conversations: any[]; total: number; page: number; limit: number }>(url, { method: 'GET' });
  }

  async getTournamentChatOneToOne(
    tournamentId: string,
    params?: { messageLimit?: number; beforeMessageId?: string; afterMessageId?: string }
  ): Promise<ApiResponse<{ conversation: any; messages: any[]; totalMessages: number }>> {
    let url = `/customers/tournaments/${encodeURIComponent(tournamentId)}/chat/conversations/1-to-1?messageLimit=${params?.messageLimit ?? 50}`;
    if (params?.beforeMessageId) url += `&beforeMessageId=${encodeURIComponent(params.beforeMessageId)}`;
    if (params?.afterMessageId) url += `&afterMessageId=${encodeURIComponent(params.afterMessageId)}`;
    return this.request<{ conversation: any; messages: any[]; totalMessages: number }>(url, { method: 'GET' });
  }

  /** Get or create tournament group chat and load messages. Use only for group chat (not 1-to-1). */
  async getTournamentChatGroup(
    tournamentId: string,
    params?: { messageLimit?: number; beforeMessageId?: string; afterMessageId?: string }
  ): Promise<ApiResponse<{ conversation: any; messages: any[]; totalMessages: number }>> {
    let url = `/customers/tournaments/${encodeURIComponent(tournamentId)}/chat/conversations/group?messageLimit=${params?.messageLimit ?? 50}`;
    if (params?.beforeMessageId) url += `&beforeMessageId=${encodeURIComponent(params.beforeMessageId)}`;
    if (params?.afterMessageId) url += `&afterMessageId=${encodeURIComponent(params.afterMessageId)}`;
    return this.request<{ conversation: any; messages: any[]; totalMessages: number }>(url, { method: 'GET' });
  }

  async getTournamentChatMessages(
    tournamentId: string,
    conversationId: string,
    params?: { messageLimit?: number; beforeMessageId?: string; afterMessageId?: string }
  ): Promise<ApiResponse<{ messages: any[]; totalMessages: number }>> {
    let url = `/customers/tournaments/${encodeURIComponent(tournamentId)}/chat/conversations/${encodeURIComponent(conversationId)}/messages?messageLimit=${params?.messageLimit ?? 50}`;
    if (params?.beforeMessageId) url += `&beforeMessageId=${encodeURIComponent(params.beforeMessageId)}`;
    if (params?.afterMessageId) url += `&afterMessageId=${encodeURIComponent(params.afterMessageId)}`;
    return this.request<{ messages: any[]; totalMessages: number }>(url, { method: 'GET' });
  }

  async sendTournamentChatMessage(
    tournamentId: string,
    conversationId: string,
    payload: { messageText?: string; replyToMessageId?: string; attachments?: File[] }
  ): Promise<ApiResponse<{ message: any }>> {
    const { messageText, replyToMessageId, attachments } = payload;
    if ((!messageText || !messageText.trim()) && (!attachments || attachments.length === 0)) {
      return Promise.reject(new Error('Either messageText or at least one attachment is required'));
    }
    if (attachments && attachments.length > 0) {
      const formData = new FormData();
      if (messageText?.trim()) formData.append('messageText', messageText.trim());
      if (replyToMessageId) formData.append('replyToMessageId', replyToMessageId);
      attachments.forEach((file) => formData.append('attachments', file));
      const baseURL = this.baseURL.replace(/\/+$/, '');
      const normalizedEndpoint = `/customers/tournaments/${encodeURIComponent(tournamentId)}/chat/conversations/${encodeURIComponent(conversationId)}/messages`;
      const url = `${baseURL}${normalizedEndpoint}`;
      const { accessToken } = this.getTokens();
      const headers: Record<string, string> = {};
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      const response = await fetch(url, { method: 'POST', headers, body: formData });
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      const data = await response.json();
      return data;
    }
    return this.request<{ message: any }>(
      `/customers/tournaments/${encodeURIComponent(tournamentId)}/chat/conversations/${encodeURIComponent(conversationId)}/messages`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageText: messageText?.trim() || '', replyToMessageId: replyToMessageId || undefined })
      }
    );
  }

  /** Mark tournament chat conversation as read (customer). Call when user opens the chat. */
  async markTournamentChatAsRead(tournamentId: string, conversationId: string): Promise<ApiResponse<{ message?: string }>> {
    return this.request<{ message?: string }>(
      `/customers/tournaments/${encodeURIComponent(tournamentId)}/chat/conversations/${encodeURIComponent(conversationId)}/read`,
      { method: 'PUT' }
    );
  }

  /** Add like/dislike reaction on a message (tournament chat – group or 1:1). */
  async addTournamentChatReaction(
    tournamentId: string,
    conversationId: string,
    messageId: string,
    reactionType: 'like' | 'dislike'
  ): Promise<ApiResponse<{ reaction: any }>> {
    return this.request<{ reaction: any }>(
      `/customers/tournaments/${encodeURIComponent(tournamentId)}/chat/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/reaction`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reactionType })
      }
    );
  }

  /** Remove own reaction on a message (tournament chat). */
  async removeTournamentChatReaction(
    tournamentId: string,
    conversationId: string,
    messageId: string
  ): Promise<ApiResponse<{ message?: string }>> {
    return this.request<{ message?: string }>(
      `/customers/tournaments/${encodeURIComponent(tournamentId)}/chat/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/reaction`,
      { method: 'DELETE' }
    );
  }

  /** Send tournament chat typing (REST). POST .../conversations/:conversationId/typing, body { isTyping }. 204 No Content. */
  async sendTournamentChatTyping(tournamentId: string, conversationId: string, isTyping: boolean): Promise<void> {
    await this.request(
      `/customers/tournaments/${encodeURIComponent(tournamentId)}/chat/conversations/${encodeURIComponent(conversationId)}/typing`,
      { method: 'POST', body: JSON.stringify({ isTyping }) }
    );
  }

  /** Send friend (DM) chat typing (REST). POST /api/v1/customers/chat/typing, body { friendId, isTyping }. 204 No Content. */
  async sendFriendChatTyping(friendId: string, isTyping: boolean): Promise<void> {
    await this.request('/customers/chat/typing', {
      method: 'POST',
      body: JSON.stringify({ friendId, isTyping }),
    });
  }

  // Token management
  /**
   * Shared, cross-tab session — a real logged-in customer. Also clears any tab-scoped guest token,
   * because signing in supersedes the guest session this tab was running on.
   */
  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    try {
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('refreshToken');
    } catch { /* private mode */ }
  }

  /**
   * Tab-scoped session — a QR table guest. Kept out of `localStorage` so two tables open in two tabs
   * (or a customer login elsewhere) can't overwrite each other's identity. See {@link getTokens}.
   */
  setGuestTokens(accessToken: string, refreshToken: string): void {
    try {
      sessionStorage.setItem('accessToken', accessToken);
      sessionStorage.setItem('refreshToken', refreshToken || '');
    } catch {
      // Private mode with sessionStorage blocked: fall back to the shared slot so the guest can still
      // use their table, accepting the old cross-tab behaviour rather than a dead session.
      this.setTokens(accessToken, refreshToken);
    }
  }

  // ==================== Social Feed API Methods ====================

  // Create Post
  async createPost(postData: {
    title?: string;
    description?: string;
    contentText?: string;
    status?: 'draft' | 'published';
    files?: File[];
    tags?: string[];
  }): Promise<ApiResponse<any>> {
    const formData = new FormData();
    if (postData.title) formData.append('title', postData.title);
    if (postData.description) formData.append('description', postData.description);
    if (postData.contentText) formData.append('contentText', postData.contentText);
    if (postData.status) formData.append('status', postData.status);
    if (postData.tags && postData.tags.length > 0) {
      // Append tags as individual FormData entries (standard approach)
      // Backend can parse as array from multiple entries with same key
      postData.tags.forEach(tag => formData.append('tags', tag));
    }
    if (postData.files) {
      postData.files.forEach(file => formData.append('files', file));
    }

    const { accessToken } = this.getTokens();
    const baseURL = this.baseURL.replace(/\/+$/, ''); // Remove trailing slashes
    // baseURL already includes /api/v1, so don't add it again
    const normalizedEndpoint = '/customers/social/posts';
    const url = `${baseURL}${normalizedEndpoint}`;
    
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    // Don't set Content-Type for FormData, browser will set it with boundary

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Update Post
  async updatePost(postId: string, postData: {
    title?: string;
    description?: string;
    contentText?: string;
    status?: 'draft' | 'published';
    isArchived?: boolean;
    isHidden?: boolean;
    tags?: string[];
  }): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/${encodeURIComponent(postId)}`, {
      method: 'PUT',
      body: JSON.stringify(postData),
    });
  }

  // Delete Post
  async deletePost(postId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/${encodeURIComponent(postId)}`, {
      method: 'DELETE',
    });
  }

  // Get Post by ID
  async getPostById(postId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/${encodeURIComponent(postId)}`, {
      method: 'GET',
    });
  }

  // Preview Post
  async previewPost(postId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/${encodeURIComponent(postId)}/preview`, {
      method: 'GET',
    });
  }

  // Publish Post
  async publishPost(postId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/${encodeURIComponent(postId)}/publish`, {
      method: 'POST',
    });
  }

  // Track Post View
  async trackPostView(postId: string, data: {
    viewDurationSeconds?: number;
    interactionType?: 'view' | 'like' | 'comment' | 'share' | 'fullscreen';
  }): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/${encodeURIComponent(postId)}/view`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Get Algorithm-Based Feed
  async getAlgorithmFeed(params: {
    page?: number;
    limit?: number;
    feedType?: 'top' | 'following' | 'personalized' | 'interests';
    interestCategories?: string[];
  }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.feedType) queryParams.append('feedType', params.feedType);
    if (params.interestCategories && params.interestCategories.length > 0) {
      queryParams.append('interestCategories', params.interestCategories.join(','));
    }

    return this.request<any>(`/customers/social/posts/algorithm?${queryParams.toString()}`, {
      method: 'GET',
    });
  }

  // Get Customer Posts (by customer ID)
  async getCustomerPosts(customerId: string, page: number = 1, limit: number = 20): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/customers/${encodeURIComponent(customerId)}/posts?page=${page}&limit=${limit}`, {
      method: 'GET',
    });
  }

  // Get Current User's Posts (my posts)
  async getMyPosts(page: number = 1, limit: number = 20): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/my?page=${page}&limit=${limit}`, {
      method: 'GET',
    });
  }

  // Like/Unlike Post
  async likePost(postId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/${encodeURIComponent(postId)}/like`, {
      method: 'POST',
    });
  }

  async unlikePost(postId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/${encodeURIComponent(postId)}/unlike`, {
      method: 'POST',
    });
  }

  // Dislike/Undislike Post
  async dislikePost(postId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/${encodeURIComponent(postId)}/dislike`, {
      method: 'POST',
    });
  }

  async undislikePost(postId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/${encodeURIComponent(postId)}/undislike`, {
      method: 'POST',
    });
  }

  // Share Post
  async sharePost(postId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/${encodeURIComponent(postId)}/share`, {
      method: 'POST',
    });
  }

  // Get Social Feed Post Comments
  async getSocialPostComments(postId: string, page: number = 1, limit: number = 50): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/${encodeURIComponent(postId)}/comments?page=${page}&limit=${limit}`, {
      method: 'GET',
    });
  }

  // Create Social Feed Post Comment
  async createSocialPostComment(postId: string, commentData: {
    commentText: string;
    parentCommentId?: string | null;
  }): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/${encodeURIComponent(postId)}/comments`, {
      method: 'POST',
      body: JSON.stringify(commentData),
    });
  }

  // Delete Social Feed Post Comment
  async deleteSocialPostComment(postId: string, commentId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}`, {
      method: 'DELETE',
    });
  }

  // Customer Interests Management
  async getCustomerInterests(): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/social/interests', {
      method: 'GET',
    });
  }

  async addCustomerInterest(interestData: {
    interestCategory: string;
    interestValue: string;
    weight?: number;
  }): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/social/interests', {
      method: 'POST',
      body: JSON.stringify(interestData),
    });
  }

  async removeCustomerInterest(interestId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/customers/social/interests/${encodeURIComponent(interestId)}`, {
      method: 'DELETE',
    });
  }

  /** Get Ably token request for real-time (authCallback). GET /api/v1/customers/ably-token */
  async getAblyToken(): Promise<ApiResponse<any>> {
    return this.request<any>('/customers/ably-token', { method: 'GET' });
  }

  // ==================== Classifieds Marketplace (/api/v1/market/*) ====================
  // Dubizzle-style buy/sell + service directory. Distinct from the "mediator groups"
  // feature above (marketListGroups etc. → /customers/marketplace). All endpoints return
  // the { success, data } envelope; these helpers return the `data` payload.

  /** Person identity for the current user. GET /persons/me */
  async mktGetMe(): Promise<MktTypes.MarketPerson> {
    const res = await this.request<MktTypes.MarketPerson>('/persons/me', { method: 'GET' });
    return (res.data as MktTypes.MarketPerson);
  }

  /** Public person profile. GET /persons/:id */
  async mktGetPerson(personId: string): Promise<MktTypes.MarketPerson> {
    const res = await this.request<MktTypes.MarketPerson>(`/persons/${encodeURIComponent(personId)}`, { method: 'GET' });
    return (res.data as MktTypes.MarketPerson);
  }

  /** Categories, optionally scoped to a kind. GET /market/categories?kind= */
  async mktGetCategories(kind?: MktTypes.MarketKind): Promise<MktTypes.MarketCategory[]> {
    const qs = kind ? `?kind=${encodeURIComponent(kind)}` : '';
    const res = await this.request<{ categories: MktTypes.MarketCategory[] }>(`/market/categories${qs}`, { method: 'GET' });
    return res.data?.categories ?? [];
  }

  /** Browse listings (cursor-paged). GET /market/listings */
  async mktGetListings(params: {
    kind?: MktTypes.MarketKind;
    category?: string;
    q?: string;
    city?: string;
    seller?: string;
    cursor?: string;
  } = {}): Promise<{ listings: MktTypes.MarketListingSummary[]; nextCursor?: string | null }> {
    const sp = new URLSearchParams();
    if (params.kind) sp.set('kind', params.kind);
    if (params.category) sp.set('category', params.category);
    if (params.seller) sp.set('seller', params.seller);
    if (params.q) sp.set('q', params.q);
    if (params.city) sp.set('city', params.city);
    if (params.cursor) sp.set('cursor', params.cursor);
    const qs = sp.toString();
    const res = await this.request<{ listings: MktTypes.MarketListingSummary[]; nextCursor?: string | null }>(
      `/market/listings${qs ? `?${qs}` : ''}`,
      { method: 'GET' }
    );
    return { listings: res.data?.listings ?? [], nextCursor: res.data?.nextCursor ?? null };
  }

  /** Listing detail. GET /market/listings/:id */
  async mktGetListing(id: string): Promise<MktTypes.MarketListing> {
    const res = await this.request<{ listing: MktTypes.MarketListing }>(`/market/listings/${encodeURIComponent(id)}`, { method: 'GET' });
    return (res.data?.listing as MktTypes.MarketListing);
  }

  /** Create a listing. POST /market/listings */
  async mktCreateListing(body: MktTypes.CreateMarketListingInput): Promise<MktTypes.MarketListing> {
    const res = await this.request<{ listing: MktTypes.MarketListing }>('/market/listings', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return (res.data?.listing as MktTypes.MarketListing);
  }

  /** Update a listing. PATCH /market/listings/:id */
  async mktUpdateListing(id: string, body: Partial<MktTypes.CreateMarketListingInput>): Promise<MktTypes.MarketListing> {
    const res = await this.request<{ listing: MktTypes.MarketListing }>(`/market/listings/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    return (res.data?.listing as MktTypes.MarketListing);
  }

  /** Change a listing's status. POST /market/listings/:id/status */
  async mktSetListingStatus(id: string, status: MktTypes.MarketListingStatus): Promise<any> {
    const res = await this.request<any>(`/market/listings/${encodeURIComponent(id)}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    });
    return res.data;
  }

  /** My own listings. GET /market/my/listings */
  async mktGetMyListings(): Promise<MktTypes.MarketListingSummary[]> {
    const res = await this.request<{ listings: MktTypes.MarketListingSummary[] }>('/market/my/listings', { method: 'GET' });
    return res.data?.listings ?? [];
  }

  /** Upload one listing photo OR video (multipart, field "image"). POST /market/upload -> { url, mediaType } */
  async mktUploadMedia(file: File): Promise<{ url: string; mediaType: 'image' | 'video' }> {
    const formData = new FormData();
    formData.append('image', file);
    const baseURL = this.baseURL.replace(/\/+$/, '');
    const url = `${baseURL}/market/upload`;
    const { accessToken } = this.getTokens();
    const headers: Record<string, string> = {};
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    const response = await fetch(url, { method: 'POST', headers, body: formData });
    if (!response.ok) {
      const errText = await response.text();
      let msg = `HTTP ${response.status}`;
      try { const j = JSON.parse(errText); msg = j?.error?.message || j.message || msg; } catch { msg = errText || msg; }
      throw new Error(msg);
    }
    const json = await response.json();
    const data = json?.data ?? json ?? {};
    return { url: data.url, mediaType: (data.mediaType === 'video' ? 'video' : 'image') };
  }

  /** Upload one photo (multipart, field "image"). POST /market/upload -> url */
  async mktUploadPhoto(file: File): Promise<string> {
    const { url } = await this.mktUploadMedia(file);
    return url;
  }

  /** Reviews for a person. GET /market/persons/:personId/reviews */
  async mktGetPersonReviews(personId: string): Promise<{ reviews: MktTypes.MarketReview[]; avg: number; count: number }> {
    const res = await this.request<{ reviews: MktTypes.MarketReview[]; avg: number; count: number }>(
      `/market/persons/${encodeURIComponent(personId)}/reviews`,
      { method: 'GET' }
    );
    return { reviews: res.data?.reviews ?? [], avg: res.data?.avg ?? 0, count: res.data?.count ?? 0 };
  }

  /** Leave a review for a person. POST /market/persons/:personId/reviews */
  async mktPostPersonReview(
    personId: string,
    body: { rating: number; comment?: string; listingId?: string }
  ): Promise<MktTypes.MarketReview> {
    const res = await this.request<{ review: MktTypes.MarketReview }>(
      `/market/persons/${encodeURIComponent(personId)}/reviews`,
      { method: 'POST', body: JSON.stringify(body) }
    );
    return (res.data?.review as MktTypes.MarketReview);
  }

  /** Start (or reuse) a contact thread on a listing. POST /market/listings/:id/contact */
  async mktContactSeller(listingId: string): Promise<MktTypes.MarketThreadSummary> {
    const res = await this.request<{ thread: MktTypes.MarketThreadSummary }>(
      `/market/listings/${encodeURIComponent(listingId)}/contact`,
      { method: 'POST' }
    );
    return (res.data?.thread as MktTypes.MarketThreadSummary);
  }

  /** My message threads. GET /market/threads */
  async mktGetThreads(): Promise<MktTypes.MarketThreadSummary[]> {
    const res = await this.request<{ threads: MktTypes.MarketThreadSummary[] }>('/market/threads', { method: 'GET' });
    return res.data?.threads ?? [];
  }

  /** Messages in a thread. GET /market/threads/:id/messages */
  async mktGetThreadMessages(threadId: string): Promise<MktTypes.MarketMessage[]> {
    const res = await this.request<{ messages: MktTypes.MarketMessage[] }>(
      `/market/threads/${encodeURIComponent(threadId)}/messages`,
      { method: 'GET' }
    );
    return res.data?.messages ?? [];
  }

  /** Send a message in a thread. POST /market/threads/:id/messages */
  async mktSendThreadMessage(threadId: string, text: string): Promise<MktTypes.MarketMessage> {
    const res = await this.request<{ message: MktTypes.MarketMessage }>(
      `/market/threads/${encodeURIComponent(threadId)}/messages`,
      { method: 'POST', body: JSON.stringify({ text }) }
    );
    return (res.data?.message as MktTypes.MarketMessage);
  }

  /** Service provider directory. GET /market/providers */
  async mktGetProviders(params: { category?: string; city?: string; q?: string } = {}): Promise<MktTypes.MarketProvider[]> {
    const sp = new URLSearchParams();
    if (params.category) sp.set('category', params.category);
    if (params.city) sp.set('city', params.city);
    if (params.q) sp.set('q', params.q);
    const qs = sp.toString();
    const res = await this.request<{ providers: MktTypes.MarketProvider[] }>(
      `/market/providers${qs ? `?${qs}` : ''}`,
      { method: 'GET' }
    );
    return res.data?.providers ?? [];
  }

  // ---- Trust & safety: block / report ----

  /** Block a person. POST /market/persons/:personId/block -> { blocked: true } */
  async mktBlockPerson(personId: string): Promise<boolean> {
    const res = await this.request<{ blocked: boolean }>(
      `/market/persons/${encodeURIComponent(personId)}/block`,
      { method: 'POST' }
    );
    return res.data?.blocked ?? true;
  }

  /** Unblock a person. DELETE /market/persons/:personId/block -> { blocked: false } */
  async mktUnblockPerson(personId: string): Promise<boolean> {
    const res = await this.request<{ blocked: boolean }>(
      `/market/persons/${encodeURIComponent(personId)}/block`,
      { method: 'DELETE' }
    );
    return res.data?.blocked ?? false;
  }

  /** List person ids the current user has blocked. GET /market/blocks -> { blocked: [personId] } */
  async mktGetBlocks(): Promise<string[]> {
    const res = await this.request<{ blocked: string[] }>('/market/blocks', { method: 'GET' });
    return res.data?.blocked ?? [];
  }

  /** Report a listing. POST /market/listings/:id/report -> { ok: true } */
  async mktReportListing(
    listingId: string,
    reason: MktTypes.MarketReportReason,
    note?: string
  ): Promise<boolean> {
    const res = await this.request<{ ok: boolean }>(
      `/market/listings/${encodeURIComponent(listingId)}/report`,
      { method: 'POST', body: JSON.stringify({ reason, note: note || undefined }) }
    );
    return res.data?.ok ?? true;
  }

  // ---- Saved ads (favorites) ----

  /** Save a listing to favorites. POST /market/listings/:id/save -> { saved: true } */
  async mktSaveListing(id: string): Promise<boolean> {
    const res = await this.request<{ saved: boolean }>(
      `/market/listings/${encodeURIComponent(id)}/save`,
      { method: 'POST' }
    );
    return res.data?.saved ?? true;
  }

  /** Remove a listing from favorites. DELETE /market/listings/:id/save -> { saved: false } */
  async mktUnsaveListing(id: string): Promise<boolean> {
    const res = await this.request<{ saved: boolean }>(
      `/market/listings/${encodeURIComponent(id)}/save`,
      { method: 'DELETE' }
    );
    return res.data?.saved ?? false;
  }

  /** My saved listing cards. GET /market/saved -> { listings: [...] } */
  async mktGetSaved(): Promise<MktTypes.MarketListingSummary[]> {
    const res = await this.request<{ listings: MktTypes.MarketListingSummary[] }>(
      '/market/saved',
      { method: 'GET' }
    );
    return res.data?.listings ?? [];
  }

  /** Ids of my saved listings (for hydrating card heart state). GET /market/saved/ids -> { ids: [...] } */
  async mktGetSavedIds(): Promise<string[]> {
    const res = await this.request<{ ids: string[] }>('/market/saved/ids', { method: 'GET' });
    return res.data?.ids ?? [];
  }

  // ==================== Order Management (v1 cross-repo contract) ====================
  // These hit the top-level /api/v1 mount (NOT /customers/...). Responses are the
  // canonical shapes from the contract (Order, ChatMessage, Paged<T>, ...), not the
  // legacy { success, data } envelope — so we return the raw parsed body.

  /** Selectable target organizer branches for order creation. GET /branches */
  async getOrderBranches(): Promise<OrderTypes.OrderBranch[]> {
    const res = await this.request<any>('/branches', { method: 'GET' });
    // Tolerate either a bare array or { items: [...] }.
    const body: any = res;
    if (Array.isArray(body)) return body as OrderTypes.OrderBranch[];
    if (Array.isArray(body?.items)) return body.items as OrderTypes.OrderBranch[];
    if (Array.isArray(body?.data)) return body.data as OrderTypes.OrderBranch[];
    return [];
  }

  /** Create an order. Customer supplies items only; server assigns orderNumber + CREATED. */
  async createOrder(body: {
    branchId: string;
    note?: string;
    items: OrderTypes.NewOrderItemInput[];
  }): Promise<OrderTypes.Order> {
    const res = await this.request<any>('/orders', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return res as unknown as OrderTypes.Order;
  }

  /** Submit a CREATED order to its branch (makes it visible to the organizer). */
  async submitOrder(orderId: string): Promise<OrderTypes.Order> {
    const res = await this.request<any>(`/orders/${encodeURIComponent(orderId)}/submit`, {
      method: 'POST',
    });
    return res as unknown as OrderTypes.Order;
  }

  /** Cancel an order (owner only, before DELIVERED). */
  async cancelOrder(orderId: string): Promise<OrderTypes.Order> {
    const res = await this.request<any>(`/orders/${encodeURIComponent(orderId)}/cancel`, {
      method: 'POST',
    });
    return res as unknown as OrderTypes.Order;
  }

  /** List my orders (paginated). */
  async getMyOrders(params?: { page?: number; pageSize?: number; status?: string }): Promise<OrderTypes.Paged<OrderTypes.Order>> {
    const search = new URLSearchParams();
    search.set('role', 'customer');
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.pageSize != null) search.set('pageSize', String(params.pageSize));
    if (params?.status) search.set('status', params.status);
    const res = await this.request<any>(`/orders?${search.toString()}`, { method: 'GET' });
    const body: any = res;
    if (Array.isArray(body)) return { items: body, page: 1, total: body.length };
    return body as OrderTypes.Paged<OrderTypes.Order>;
  }

  /** Full order incl. items + event timeline. */
  async getOrder(orderId: string): Promise<OrderTypes.Order> {
    const res = await this.request<any>(`/orders/${encodeURIComponent(orderId)}`, { method: 'GET' });
    return res as unknown as OrderTypes.Order;
  }

  /** Comment on a DELIVERED|CLOSED order. Stored as a COMMENT chat message. */
  async commentOnOrder(orderId: string, body: string): Promise<OrderTypes.ChatMessage> {
    const res = await this.request<any>(`/orders/${encodeURIComponent(orderId)}/comment`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    });
    return res as unknown as OrderTypes.ChatMessage;
  }

  /** Chat threads visible to the caller. */
  async getChatThreads(): Promise<OrderTypes.ChatThread[]> {
    const res = await this.request<any>('/chat/threads', { method: 'GET' });
    const body: any = res;
    if (Array.isArray(body)) return body as OrderTypes.ChatThread[];
    if (Array.isArray(body?.items)) return body.items as OrderTypes.ChatThread[];
    return [];
  }

  /** Create (or fetch) the thread between this customer and a branch. */
  async createChatThread(branchId: string): Promise<OrderTypes.ChatThread> {
    const res = await this.request<any>('/chat/threads', {
      method: 'POST',
      body: JSON.stringify({ branchId }),
    });
    return res as unknown as OrderTypes.ChatThread;
  }

  /** Paginated messages for a thread. `after` is the ISO cursor (nextCursor) to page forward. */
  async getChatMessages(
    threadId: string,
    after?: string
  ): Promise<{ items: OrderTypes.ChatMessage[]; nextCursor?: string | null }> {
    const query = after ? `?after=${encodeURIComponent(after)}` : '';
    const res = await this.request<any>(`/chat/threads/${encodeURIComponent(threadId)}/messages${query}`, {
      method: 'GET',
    });
    const body: any = res;
    if (Array.isArray(body)) return { items: body };
    return { items: body?.items ?? [], nextCursor: body?.nextCursor ?? null };
  }

  /** Post a TEXT message or share an order (ORDER_REF requires orderId). */
  async postChatMessage(
    threadId: string,
    payload: { type: 'TEXT' | 'ORDER_REF'; body?: string; orderId?: string }
  ): Promise<OrderTypes.ChatMessage> {
    const res = await this.request<any>(`/chat/threads/${encodeURIComponent(threadId)}/messages`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return res as unknown as OrderTypes.ChatMessage;
  }

  /** Decode the userId from the JWT access token (no verification — UI use only). */
  getCurrentUserId(): string | null {
    const { accessToken } = this.getTokens();
    if (!accessToken) return null;
    try {
      const part = accessToken.split('.')[1];
      if (!part) return null;
      const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
      const claims = JSON.parse(json);
      return claims.userId || claims.sub || claims.id || null;
    } catch {
      return null;
    }
  }

  /**
   * A QR table session belongs to ONE TAB, not to the browser.
   *
   * `localStorage` is shared by every tab on this origin, so when it held the guest token a second
   * tab — another table's QR, or a normal customer login — silently repointed the identity of every
   * open tab while each kept its own `bookingId` in React state. The next action then failed with
   * "This booking is not yours". Guest tokens therefore live in `sessionStorage`, which is per-tab,
   * and are preferred over the shared slot when present.
   *
   * Logged-in customers are unchanged: they stay in `localStorage` and stay shared across tabs.
   */
  getTokens(): { accessToken: string | null; refreshToken: string | null } {
    const guestAccess = sessionStorage.getItem('accessToken');
    if (guestAccess) {
      return { accessToken: guestAccess, refreshToken: sessionStorage.getItem('refreshToken') };
    }
    return {
      accessToken: localStorage.getItem('accessToken'),
      refreshToken: localStorage.getItem('refreshToken'),
    };
  }

  /** True when this tab is running on its own (QR guest) session rather than the shared one. */
  private hasTabScopedSession(): boolean {
    try { return !!sessionStorage.getItem('accessToken'); } catch { return false; }
  }

  /**
   * The current tab-scoped (QR guest) access token, if any. Capture this BEFORE logging in — a
   * registered login (setTokens) clears the guest token, so "claim my session" must snapshot it
   * first and pass it as the ownership proof. Returns null when this tab isn't a guest session.
   */
  getGuestAccessToken(): string | null {
    try { return sessionStorage.getItem('accessToken'); } catch { return null; }
  }

  /**
   * Pending "claim my session" intent. Set (with the snapshotted guest token) just before sending a
   * QR guest to /login; read by the app-level claim runner once they return authenticated. Stored in
   * localStorage so it survives the full-page nav to /login, and timestamped so a stale/abandoned
   * intent is ignored rather than firing on some unrelated later login.
   */
  private static PENDING_CLAIM_KEY = 'qrClaimIntent';
  setPendingClaim(guestToken: string): void {
    try { localStorage.setItem(ApiService.PENDING_CLAIM_KEY, JSON.stringify({ guestToken, ts: Date.now() })); } catch { /* private mode */ }
  }
  getPendingClaim(): { guestToken: string; ts: number } | null {
    try {
      const raw = localStorage.getItem(ApiService.PENDING_CLAIM_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.guestToken === 'string' && typeof parsed.ts === 'number') return parsed;
      return null;
    } catch { return null; }
  }
  clearPendingClaim(): void {
    try { localStorage.removeItem(ApiService.PENDING_CLAIM_KEY); } catch { /* private mode */ }
  }

  /**
   * Write refreshed tokens back to whichever scope the request was authenticated with, so a token
   * refresh on a guest tab cannot promote that guest into the shared, cross-tab slot.
   */
  private setTokensInActiveScope(accessToken: string, refreshToken: string): void {
    if (this.hasTabScopedSession()) this.setGuestTokens(accessToken, refreshToken);
    else this.setTokens(accessToken, refreshToken);
  }

  clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    try {
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('refreshToken');
    } catch { /* private mode */ }
    // Drop the remembered page too, so a prior (e.g. QR guest) session can't redirect the next login.
    localStorage.removeItem(ApiService.LAST_PATH_KEY);
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const { accessToken } = this.getTokens();
    return !!accessToken;
  }

  /** Persist last visited path so reopening the portal or FCM click can restore it. */
  private static LAST_PATH_KEY = 'customerPortalLastPath';

  /** Transient/entry routes that must never be restored as a "last visited page". */
  private isTransientPath(path: string): boolean {
    return !path || path === '/' ||
      path.startsWith('/login') || path.startsWith('/signup') || path.startsWith('/oauth') ||
      path.startsWith('/t/') || path.startsWith('/r/') || path.startsWith('/rg/') || path.startsWith('/qr'); // QR landings — die on checkout, must not be replayed
  }

  getLastPath(): string {
    if (typeof window === 'undefined') return '/customer/friends';
    const stored = localStorage.getItem(ApiService.LAST_PATH_KEY);
    // Guard against a stale QR/auth path saved by an older build (would loop back to a dead QR link).
    if (stored && this.isTransientPath(stored)) {
      localStorage.removeItem(ApiService.LAST_PATH_KEY);
      return '/customer/friends';
    }
    return stored || '/customer/friends';
  }

  setLastPath(path: string): void {
    if (typeof window === 'undefined') return;
    if (this.isTransientPath(path)) return;
    localStorage.setItem(ApiService.LAST_PATH_KEY, path);
  }

  clearLastPath(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(ApiService.LAST_PATH_KEY);
  }

  /** Last chat state for restoring same conversation when user reopens portal (FCM doc §3.2). */
  private static LAST_CHAT_KEY = 'customerPortalLastChat';

  getLastChat(): { type: string; conversationId: string; [k: string]: unknown } | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem(ApiService.LAST_CHAT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  setLastChat(payload: { type: string; conversationId: string; [k: string]: unknown }): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(ApiService.LAST_CHAT_KEY, JSON.stringify({ ...payload, openedAt: new Date().toISOString() }));
    } catch {}
  }

  clearLastChat(): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(ApiService.LAST_CHAT_KEY);
    } catch {}
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
